import {
  automationsOptions,
  automationsPointer,
  type AutomationsRoute,
} from '@src/components/automations/automations-pointer';
import { pointerForWebUrl, webUrlFromPointer } from '@sdk';
import {
  Agent,
  AgenticProcess,
  ClaudeSession,
  FlowpadDiagnosis,
  CredentialsSubview,
  GraphWorkflow,
  Layout,
  PageId,
  Project,
  RemoteWorkerSession,
  Shell,
  TypeId,
  VFSPath,
  WorldViewProjection,
  isWorldViewProjection,
  normalizeRetiredDockPointer,
  normalizeWorldViewDockPointer,
  type IDockPointer,
  type WorldViewProjection as WorldViewProjectionName,
} from '@sdk';
import { VIEW_SLOTS, ViewSlot, ViewType, VIEWER_REGISTRY } from '../types/ViewType';
import { NavigationError, NavigationErrorType } from './NavigationError';
import { buildDockUrl, isRootAddress, parseDockUrl, parseQueryParams, rootDockAddress } from './url-builder';
import { isValidView } from './validators';
import { isWorkspaceChildDock } from './adoptable-child-dock';
import {
  AssetEditor,
  AssetMode,
  projectLandingPageOf,
  type ProjectLandingPage,
  AssetRoutingMethod,
  editorForPath,
  editorForType,
  LOCAL_COMPUTE_NODE,
} from './asset-doc-types';
import {
  assetEditorOf,
  assetEditorValue,
  assetWikiValue,
  normalizeAssetVfsPath,
  parseAssetWikiRef,
  serializeAssetDocPointer,
  type AssetWikiRef,
} from './asset-doc-pointer-grammar';
import {
  ALL_SCOPE_FILTER,
  dockOptionsToScopeFilter,
  pinnedProjectId,
  projectScope,
  scopeFilterKey,
  scopeFilterToDockOptions,
  withScopeFilterOptions,
  withoutScopeFilterOptions,
  type ScopeFilter,
} from '@src/lib/scope-filter';
import { dockOptionsToSideWindows, withSideWindowsOptions, type SideWindowsState } from '@src/lib/side-windows';
import type { ViewMode } from '@src/contexts/view-mode-context';
import { DEFAULT_WORLDVIEW_COLOR_MODE, type WorldViewColorMode } from '@src/types/WorldViewColorMode';
import { DEFAULT_GRAPH_PRESENTATION, type GraphPresentation } from '@src/types/GraphPresentation';
import { credentialsPointer } from '@src/components/credentials-view/credentials-pointer';
import { dataSourcesPointer, type DataSourcesRoute } from '@src/components/data-sources/data-sources-pointer';
import { schemaTreePointer } from '@src/components/schema-tree/schema-tree-pointer';
import type { WorkerType } from '@src/hooks/use-transcript';

/**
 * URL query-param key carrying the "highlight this thing" intent across the
 * app — the single source of truth for the WikiTip highlight (see
 * docs/wikitip.md). Mirrors the existing `selected` option: URL-carried so the
 * highlight is shareable + back-button-safe. Pairs with `DockPointer.highlight`
 * / `withHighlight()` (dock surfaces) and `useHighlight()` (the home root `/`,
 * which is not a dock URL).
 */
/**
 * URL query-param keys shared by the two run-history surfaces —
 * `/dock/process-runs` and the graph-workflow studio's runs panel. Kept
 * together (and used by both) so "which run is open" means one thing across
 * the app, and so a deep link can move between the scoped and unscoped views
 * without a key translation in the middle.
 */
export const RUN_PARAM = 'run';
/** Which MessageThread a conversation view is filtered to (`?thread=<id>`). */
export const THREAD_PARAM = 'thread';
/** Agent mailbox scope carried from an agent's Stream Inbox into its conversation. */
export const AGENT_PARAM = 'agent';
export const NODE_PARAM = 'node';
export const PANEL_PARAM = 'panel';

/** The graph-workflow studio's panel tabs, as they appear in the URL. */
export type GraphWorkflowPanel = 'palette' | 'inject' | 'runs';

/**
 * How a runs list may be narrowed. These key names are the BACKEND's
 * (`flow_sdk/server/routes/runs.py:SCOPES`) verbatim — the view forwards them
 * to `GET /api/v1/runs` untranslated, so adding a scope is one entry at each
 * end and no mapping table in between.
 */
export const PROCESS_RUN_SCOPE_KEYS = [
  'project_id',
  'deployment_id',
  'flow_id',
  'flow_run_id',
  'node_id',
  'agent',
  // Paired with SCOPES in flow_sdk/server/routes/runs.py — a new scope is one
  // entry in each. An ingest worker has no spawning entity to browse from (the
  // whole reason this list exists), so its source is the only handle on it.
  'data_source_id',
  // A scheduled agent run: its trigger is what spawned it.
  'trigger_id',
] as const;

export type ProcessRunScope = Partial<Record<(typeof PROCESS_RUN_SCOPE_KEYS)[number], string>>;
export type ProcessRunsPointerOptions = ProcessRunScope & { run?: string | null };

export const HIGHLIGHT_PARAM = 'highlight';
/** An entry of an `fs/` folder to show selected. */
export const FS_SELECT_PARAM = 'select';
/** A transcript entry to scroll to and select. */
export const TRANSCRIPT_ENTRY_PARAM = 'transcript_entry_id';
/** A timestamp to seek a transcript to. */
export const TRANSCRIPT_TIME_PARAM = 't';
export const VIEW_MODE_PARAM = 'viewMode';

/**
 * The agentic process whose DISPLAY is showing this dock — the vibe workspace
 * hosting it. Written by whoever opens the content (`flow show`, a click inside
 * the workspace), read by the loader to stamp `parent_tab_id`, so host identity
 * is a fact of the URL instead of something re-derived from ambient state.
 *
 * It rides in `options`, and is therefore EXCLUDED from `tabHash` for the same
 * reason `layout` and `viewMode` are: which workspace is showing a document is
 * presentation context, not the document's identity. One document is one tab
 * however many agents display it, existing rows keep their stored pointer
 * (no migration), and the backend never sees the composite form.
 *
 * Where the URL can nest it, it is spelled as path segments —
 * `/dock/project/<P>/process/<typeid>/display/<tail>` — which `fromUrl` lifts
 * into this option and `toUrl` puts back. A hosted dock with no project segment
 * to nest under (a terminal, a web app) keeps the plain `?host=` query form.
 * Pairs with `DockPointer.hostProcessId` / `withHost()`.
 */
export const HOST_PARAM = 'host';

/** Path markers for {@link HOST_PARAM}: `<projectId>/process/<typeid>/display/<tail>`. */
const HOST_SEGMENT = 'process';
const HOST_DISPLAY_SEGMENT = 'display';

/**
 * A view NESTED in an asset editor — the agent editor's resources (an MCP server, a skill, a doc,
 * a channel, a data source, a schedule) opened in place of its body, in the same tab. Spelled as a
 * path suffix so the chain reads in the URL (`…/agent-<id>/child/mcp/mcp-<id>`), carried as two
 * options so the asset pointer every other reader parses is the parent's untouched — the same
 * lift/embed as the workspace host. Options stay out of `tabHash`: a child never opens a tab.
 */
export const CHILD_PARAM = 'child';
export const CHILD_SECTION_PARAM = 'child_section';
const CHILD_SEGMENT = 'child';
/** The sections a child can be opened from. */
export const CHILD_SECTIONS = [
  'channel',
  'data_source',
  'schedule',
  'mcp',
  'skill',
  'doc',
  'deployment',
  'credential',
] as const;
export type ChildSection = (typeof CHILD_SECTIONS)[number];

function isChildSection(value: string | undefined): value is ChildSection {
  return !!value && (CHILD_SECTIONS as readonly string[]).includes(value);
}

/** Lift a trailing `child/<section>/<typeid>` off a pointer. Pointers without one pass through. */
function liftChild(pointer: string | undefined): {
  pointer: string | undefined;
  section: ChildSection | null;
  child: string | null;
} {
  if (!pointer) return { pointer, section: null, child: null };
  const seg = pointer.split('/');
  const at = seg.length - 3;
  if (at < 1 || seg[at] !== CHILD_SEGMENT || !isChildSection(seg[at + 1]) || !seg[at + 2]) {
    return { pointer, section: null, child: null };
  }
  return { pointer: seg.slice(0, at).join('/'), section: seg[at + 1] as ChildSection, child: seg[at + 2] };
}

/**
 * Lift `process/<typeid>/display/` out of a PROJECT pointer, returning the
 * host-free pointer plus the host it carried. The inverse of
 * {@link embedHostInProjectPointer}. Host-free pointers pass through untouched,
 * so every existing project URL parses exactly as before.
 */
function liftHostFromProjectPointer(pointer: string | undefined): {
  pointer: string | undefined;
  hostProcessId: string | null;
} {
  if (!pointer) return { pointer, hostProcessId: null };
  const seg = pointer.split('/');
  // [0]=<projectId> [1]='process' [2]=<typeid> [3]='display' [4…]=the tail
  if (seg.length < 5 || seg[1] !== HOST_SEGMENT || seg[3] !== HOST_DISPLAY_SEGMENT) {
    return { pointer, hostProcessId: null };
  }
  return { pointer: [seg[0], ...seg.slice(4)].join('/'), hostProcessId: seg[2] || null };
}

/**
 * Put the host back into a PROJECT pointer as path segments. Returns the
 * pointer unchanged when there is no host, or when the pointer addresses the
 * project itself — a host with nothing displayed is not an address.
 */
function embedHostInProjectPointer(pointer: string | undefined, hostProcessId: string | null): string | undefined {
  if (!pointer || !hostProcessId) return pointer;
  const slash = pointer.indexOf('/');
  if (slash < 0) return pointer;
  const projectId = pointer.slice(0, slash);
  const tail = pointer.slice(slash + 1);
  if (!tail) return pointer;
  return `${projectId}/${HOST_SEGMENT}/${hostProcessId}/${HOST_DISPLAY_SEGMENT}/${tail}`;
}

/**
 * URL query-param key selecting which translated body of an asset to show. It
 * carries a language code (`es`, `he`, `fr-CA`, …); absent means the original
 * doc. Like the other option params it rides in `options` and is deliberately
 * EXCLUDED from `tabHash`, so switching languages swaps the body inline in the
 * SAME tab (no new tab). Pairs with `DockPointer.lang` / `withLang()`; the
 * asset editor reads it to point at the matching `translations[].ref`.
 */
export const LANG_PARAM = 'lang';

/**
 * URL query-param key paging a conversation list backwards: the `updated_date` (ISO) the page
 * shows conversations OLDER than. Absent = now — the latest page, live. Rides in `options`, so it
 * is reload- and back-safe and never opens a tab. Pairs with `DockPointer.pageBefore` /
 * `withPageBefore()`; the stream inbox reads it to build its page query.
 */
export const PAGE_BEFORE_PARAM = 'before';

/**
 * URL query-param key carrying the ACTIVE USER JOURNEY across the app. Null by
 * default; when set, the guided journey tray is shown for that journey and the
 * orchestrator drives its current step. Like the other option params it rides in
 * `options` (so it is reload- and back-button-safe, and deliberately EXCLUDED
 * from `tabHash` — showing a journey never spawns a tab). It is TOPMOST/sticky:
 * `openDock` carries it onto any target that doesn't set one, so the journey
 * stays visible across navigation until explicitly closed. Pairs with
 * `DockPointer.journeyId` / `withJourney()`, `useActiveJourneyId()` (home root),
 * and `navigation.showJourney()` / `closeJourney()`.
 */
export const JOURNEY_PARAM = 'journeyId';
/**
 * Which step of that journey, 1-based. THE journey's position — there is no
 * cursor anywhere else.
 *
 * Position used to live in the journal (a `node_id` on a server row), while the
 * screen was composed by merging a partial step onto wherever the user already
 * was. Nothing in that was addressable, so nothing was reproducible: the same
 * step rendered differently depending on how you got there. A number in the URL
 * is reloadable, shareable, and the same every time.
 */
export const JOURNEY_STEP_PARAM = 'journeyStep';

/**
 * URL query-param key naming the ACTIVE workspace — a folder of related projects
 * (`flow_sdk/builtin/workspace.py`). Its value is the Workspace id; absent means the
 * default workspace ("Flowpad"), so every URL from before workspaces existed still
 * means exactly what it meant. Sticky (see `STICKY_OPTION_PARAMS`): it rides every
 * navigation until `navigation.openWorkspace()` changes it. Read through
 * `useActiveWorkspace()` — the active workspace is derived from the URL, never
 * written into context. Not the Vibe workspace `host` (`HOST_PARAM`).
 */
export const WORKSPACE_PARAM = 'workspace';
export const WORKSPACE_ENTRY_PARAM = 'workspaceEntry';

/**
 * URL query-param key naming the capability the user was reaching for when they
 * were routed to the Capabilities view — e.g. clicking "Start Codex" on an
 * opener whose harness looks unavailable lands here with
 * `capability=harness.codex.cli`. The view re-probes THAT kind on arrival, so a
 * CLI installed since the last discovery sweep is found at the moment the user
 * asks for it (the sweep only runs at backend start). Absent means "no intent"
 * — the view then shows the last known state without probing anything.
 *
 * Rides in `options` (reload- and back-safe) and is excluded from `tabHash`,
 * so arriving with an intent reuses the one Capabilities tab. Pairs with
 * `DockPointer.capabilityKind` and `navigation.openTab(ViewType.CAPABILITIES,
 * { capabilityKind })`.
 */
export const CAPABILITY_PARAM = 'capability';

/**
 * A command the shell dock hands its terminal to type AND submit on first attach.
 *
 * Consumed exactly once, by the mounted `InteractiveTerminal` at the moment
 * its PTY reports ready, which then navigates to the same dock WITHOUT the
 * param (`withoutShellStartCommand`) so a reload cannot retype it.
 */
/** The Shell entity's type token — the `shell-` prefix on a shell TypeId. */
const SHELL_TYPE = 'shell';

export const START_COMMAND_PARAM = 'startCommand';

/**
 * Canonicalize an entity-relative path: forward slashes, collapsed separators,
 * no leading/trailing slash. Route identity itself is always carried by
 * VFSPath; this helper remains for entitySubPath and legacy-route ingestion.
 */
export function normalizeRel(path: string | null | undefined): string {
  if (!path) return '';
  return path.replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
}

export const ASSET_COMPARE_POINTER_PREFIX = 'asset-compare/';

export interface AssetComparePointerPayload {
  computeNodeId: string;
  workdir: string;
  file: string;
  assetPath: string;
  assetType: string;
  assetLabel: string;
}

function encodePointerJson(value: unknown): string {
  const json = JSON.stringify(value);
  return btoa(unescape(encodeURIComponent(json)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function decodePointerJson<T>(value: string): T | null {
  try {
    const padded = value + '='.repeat((4 - (value.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

export function decodeAssetComparePointer(pointer: string | null | undefined): AssetComparePointerPayload | null {
  if (!pointer?.startsWith(ASSET_COMPARE_POINTER_PREFIX)) return null;
  return decodePointerJson<AssetComparePointerPayload>(pointer.slice(ASSET_COMPARE_POINTER_PREFIX.length));
}

/** `ViewMode.Vibe` — spelled here because this module imports the enum as a type only. */
const VIBE_MODE = 'vibe' as ViewMode;

function isViewMode(value: string | undefined): value is ViewMode {
  return value === 'vibe' || value === 'standard' || value === 'advanced' || value === 'dev';
}

/**
 * Lens pointer structure for sub-routing within lens viewer
 */
export interface LensPointerParts {
  category: string; // e.g., "claude", "session"
  type: string; // e.g., "transcript"
  ref: string; // e.g., base64url encoded vfs path, session id
}

/**
 * DockPointer represents a specific location in the UI layout system
 * Parsed from URL structure: /:layout/:viewType/:pointer[?options]
 * Layout defaults to 'dock' for backward compatibility
 *
 * Core principle: Parse and validate layout URLs, apply state to viewer store
 */
export class DockPointer implements IDockPointer {
  /** The view types a SESSION is shown in: the shell dock (chat / terminal) and the
   *  Vibe host tab. One predicate, so a site asking "is this a session surface?"
   *  never has to list them. */
  static isSessionView(viewType: ViewType | string | null | undefined): boolean {
    return viewType === ViewType.SHELL || viewType === ViewType.VIBE;
  }

  /** Check if a shell pointer refers to an AgenticProcess (pointer is a TypeId like "agentic_process-<id>") */
  static isAgenticProcessPointer(pointer?: string): boolean {
    return !!pointer?.startsWith(AgenticProcess.type + TypeId.DELIMITER);
  }

  /** Extract the entity ID from an agentic_process pointer */
  static extractAgenticProcessId(pointer: string): string {
    return pointer.slice(AgenticProcess.type.length + TypeId.DELIMITER.length);
  }

  /**
   * Canonical terminal tab identity (`TerminalTab.targetTypeId`) for a
   * `/dock/shell` (or `/win/shell`) URL pointer — the single owner of the
   * shell-pointer → tab-key grammar:
   *   - `agentic_process-<id>` → TypeId(agentic_process, id)
   *   - `shell-<id>`           → TypeId(shell, id)
   *   - bare `<id>` (legacy)   → TypeId(shell, id)
   */
  static terminalTargetTypeIdForShellPointer(pointer: string): TypeId {
    if (DockPointer.isAgenticProcessPointer(pointer)) {
      return new TypeId(AgenticProcess.type, DockPointer.extractAgenticProcessId(pointer));
    }
    const shellPrefix = Shell.type + TypeId.DELIMITER;
    const shellId = pointer.startsWith(shellPrefix) ? pointer.slice(shellPrefix.length) : pointer;
    return new TypeId(Shell.type, shellId);
  }

  public readonly viewType?: ViewType | undefined;
  public readonly pointer?: string | undefined;
  public readonly options?: Record<string, string> | undefined;
  public readonly layout: Layout;
  /** Which SPA-surface this dock addresses. Defaults to `desk` (today's desktop
   *  app); a sibling of `viewType`, never folded into `pointer`. */
  public readonly page: PageId;
  /** Set ONLY on a dock rebuilt from a stored Tab.pointer, where the folded
   *  sub-pointer can no longer be inspected. A live URL dock leaves this
   *  undefined and `toJSON` derives the bit from its real pointer. */
  private storedWorkspaceContent?: boolean;
  /** The project this dock is presented IN when that is not its target's own
   *  (`flow show` beside a session of another project). Stored in Tab.pointer so the
   *  backend files the tab in that project's strip; not part of tab identity. */
  presentedIn: string | null = null;

  constructor(data: IDockPointer, layout?: Layout);
  constructor(viewType?: ViewType, pointer?: string, options?: Record<string, string>, layout?: Layout, page?: PageId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(viewTypeOrData?: any, pointerOrLayout?: any, options?: any, layout?: any, page?: any) {
    if (viewTypeOrData && typeof viewTypeOrData === 'object') {
      // IDockPointer overload
      this.viewType = viewTypeOrData.viewType as ViewType | undefined;
      this.pointer = viewTypeOrData.pointer;
      this.options = viewTypeOrData.options;
      this.layout = (pointerOrLayout as Layout) ?? Layout.DOCK;
      this.page = (viewTypeOrData.page as PageId | undefined) ?? PageId.DESK;
    } else {
      // Positional overload
      this.viewType = viewTypeOrData;
      this.pointer = pointerOrLayout as string | undefined;
      this.options = options;
      this.layout = layout ?? Layout.DOCK;
      this.page = (page as PageId | undefined) ?? PageId.DESK;
    }
    // A mode belongs to a session tab (and what it hosts). Anywhere else the
    // option is dropped at the door, so no page can be reskinned by an address.
    if (this.options?.[VIEW_MODE_PARAM] !== undefined && !this.carriesViewMode) {
      const rest = { ...this.options };
      delete rest[VIEW_MODE_PARAM];
      this.options = rest;
    }
  }

  /** Whether this dock has a view mode at all: a session's own dock, or a child
   *  its workspace hosts. Every other page has one look and states no mode. */
  get carriesViewMode(): boolean {
    return DockPointer.isSessionView(this.viewType) || !!this.options?.[HOST_PARAM];
  }

  /**
   * The scope filter carried by this dock's options, or null when none is set
   * (so callers apply their own default). This is the single generic accessor
   * for scope-in-URL across every dock — the option-key grammar lives entirely
   * in `lib/scope-filter.ts` (`dockOptionsToScopeFilter`); no dock reads the raw
   * `scope`/`user`/`projects` keys itself.
   */
  get scopeFilter(): ScopeFilter | null {
    return dockOptionsToScopeFilter(this.options);
  }

  /**
   * Clone this pointer with `scope` serialized into its options — the single
   * generic builder for scope-in-URL. Pairs with the `scopeFilter` getter.
   */
  withScopeFilter(scope: ScopeFilter): DockPointer {
    return new DockPointer(
      this.viewType,
      this.pointer,
      withScopeFilterOptions(this.options, scope),
      this.layout,
      this.page,
    );
  }

  /**
   * The project this dock pins as its active-project context (`project` scope
   * mode), or null. Reads through the scope selectors — no call site picks the
   * mode/id fields apart itself.
   */
  get scopeProjectId(): string | null {
    return pinnedProjectId(this.scopeFilter);
  }

  /**
   * Is this dock's TAB IDENTITY keyed by its scope? True for the scope-keyed
   * views (Assets, Explorer) whose `tabHash` folds every sub-pointer of one
   * scope into a single tab. Only these can be stranded by an unsatisfiable
   * scope — their tab literally cannot be minted without a live project — so
   * it's the one class of dock that needs scope repair before materialization.
   */
  get scopeKeyed(): boolean {
    return !!(this.viewType && VIEWER_REGISTRY[this.viewType]?.scopeKeyed);
  }

  /**
   * Clone this pointer with its scope removed — same surface, no scope keys.
   * The recovery for a scope that can't be satisfied (see `repairUnsatisfiableScope`
   * in main-loader): the dock keeps showing what the URL names, just unscoped,
   * instead of filtering against a project that isn't there.
   */
  withoutScopeFilter(): DockPointer {
    return new DockPointer(
      this.viewType,
      this.pointer,
      withoutScopeFilterOptions(this.options),
      this.layout,
      this.page,
    );
  }

  /**
   * The set of open side windows + the active one carried by this dock's
   * options, or null when none is set. The single generic accessor for
   * side-windows-in-URL across every dock — the option-key grammar lives
   * entirely in `lib/side-windows.ts`; no surface reads the raw
   * `sideWindows`/`activeSideWindow` keys itself. Consumed via `useSideWindows`.
   */
  get sideWindows(): SideWindowsState | null {
    return dockOptionsToSideWindows(this.options);
  }

  /**
   * Clone this pointer with the side-windows state serialized into its options —
   * the single generic builder for side-windows-in-URL. Pairs with the
   * `sideWindows` getter.
   */
  withSideWindows(state: SideWindowsState): DockPointer {
    return new DockPointer(
      this.viewType,
      this.pointer,
      withSideWindowsOptions(this.options, state),
      this.layout,
      this.page,
    );
  }

  /**
   * The wiki word this dock asks to highlight, or null when none is set. The
   * generic accessor for highlight-in-URL on dock surfaces; the home root `/`
   * reads the same `HIGHLIGHT_PARAM` via `useHighlight()` instead (it is not a
   * dock URL). See docs/wikitip.md.
   */
  /** The folder entry an `fs/` pointer opens with selected. */
  get fsSelect(): string | undefined {
    return this.options?.[FS_SELECT_PARAM];
  }

  get highlight(): string | null {
    return this.options?.[HIGHLIGHT_PARAM] ?? null;
  }

  /**
   * Clone this pointer with `highlight` serialized into its options — the
   * writer that matches the `dockPointer.highlight = <wikiword>` mental model.
   * Pairs with the `highlight` getter.
   */
  /** The transcript entry this dock selects, or null. */
  get transcriptEntryId(): string | null {
    return this.options?.[TRANSCRIPT_ENTRY_PARAM] ?? null;
  }

  /** The transcript timestamp this dock seeks to, or null. */
  get transcriptTimestamp(): string | null {
    return this.options?.[TRANSCRIPT_TIME_PARAM] ?? null;
  }

  withHighlight(wikiword: string): DockPointer {
    return new DockPointer(
      this.viewType,
      this.pointer,
      { ...this.options, [HIGHLIGHT_PARAM]: wikiword },
      this.layout,
      this.page,
    );
  }

  /** Clone this dock addressing a different SPA-surface (page). */
  withPage(page: PageId): DockPointer {
    return new DockPointer(this.viewType, this.pointer, this.options, this.layout, page);
  }

  /**
   * Page-local view-mode override carried by the URL. This never represents the
   * user's persisted default; consumers combine it with PrefKey.VIEW_MODE in the
   * view-mode context.
   */
  get viewMode(): ViewMode | null {
    // A Vibe host dock IS the vibe presentation — the mode is implied by the
    // address, not carried as an option.
    if (this.viewType === ViewType.VIBE) return VIBE_MODE;
    const value = this.options?.[VIEW_MODE_PARAM];
    return isViewMode(value) ? value : null;
  }

  /**
   * The agentic process whose display is showing this dock, or null. See
   * {@link HOST_PARAM} — this is URL-carried host identity, not a lookup.
   */
  get hostProcessId(): string | null {
    return this.options?.[HOST_PARAM] ?? null;
  }

  /** Clone this dock hosted by a process's display, or unhosted with null. */
  withHost(processId: string | null): DockPointer {
    return this.withOption(HOST_PARAM, processId);
  }

  /** Clone this dock with a page-local view-mode override, or remove it with null. */
  withViewMode(mode: ViewMode | null): DockPointer {
    const nextOptions = { ...(this.options ?? {}) };
    delete nextOptions[VIEW_MODE_PARAM];
    // A process shown in Vibe is the Vibe HOST tab (`/dock/vibe/<process>`); shown
    // any other way it is the shell dock. This is the ONE place that maps between
    // the two families, so every opener that asks for a mode lands on the right
    // one. `null` on a Vibe dock is the bare shell dock: "the same place, mode
    // unstated" — what the view-mode sync compares by.
    const isProcess = DockPointer.isAgenticProcessPointer(this.pointer);
    if (this.viewType === ViewType.VIBE && mode !== VIBE_MODE) {
      if (mode) nextOptions[VIEW_MODE_PARAM] = mode;
      return new DockPointer(ViewType.SHELL, this.pointer, nextOptions, this.layout, this.page);
    }
    if (mode === VIBE_MODE && isProcess && (this.viewType === ViewType.SHELL || this.viewType === ViewType.VIBE)) {
      return new DockPointer(ViewType.VIBE, this.pointer, nextOptions, this.layout, this.page);
    }
    if (mode) nextOptions[VIEW_MODE_PARAM] = mode;
    return new DockPointer(this.viewType, this.pointer, nextOptions, this.layout, this.page);
  }

  /**
   * The translated-body language this dock asks to show, or null for the
   * original doc. URL-carried (shareable + back-safe) and excluded from
   * `tabHash`, so language switches stay in the same tab. Pairs with
   * `withLang()`; consumed by the markdown asset editor to swap the body ref.
   */
  get lang(): string | null {
    return this.options?.[LANG_PARAM] ?? null;
  }

  /** Clone this dock pointed at a translated body, or back to the original with null. */
  withLang(lang: string | null): DockPointer {
    return this.withOption(LANG_PARAM, lang);
  }

  /** The `updated_date` a paged list shows rows older than, or null for now (the latest page). */
  get pageBefore(): string | null {
    return this.options?.[PAGE_BEFORE_PARAM] ?? null;
  }

  /** Clone this dock paged back to rows older than `before`, or back to now with null. */
  withPageBefore(before: string | null): DockPointer {
    return this.withOption(PAGE_BEFORE_PARAM, before);
  }

  /**
   * The user journey this dock is showing, or null. URL-carried (reload- and
   * back-safe) and excluded from `tabHash`, so a journey overlays the current
   * surface instead of spawning a tab. See {@link JOURNEY_PARAM}.
   */
  get journeyId(): string | null {
    return this.options?.[JOURNEY_PARAM] ?? null;
  }

  /**
   * Which step of the journey this dock is showing (1-based), or null when no
   * journey is running. See {@link JOURNEY_STEP_PARAM}.
   *
   * Returns null for anything that is not a positive integer, so a hand-edited
   * `?journeyStep=abc` reads as "no position" rather than throwing or landing
   * the tray on NaN.
   */
  get journeyStep(): number | null {
    const raw = this.options?.[JOURNEY_STEP_PARAM];
    if (!raw) return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 ? n : null;
  }

  /**
   * The capability kind this dock was opened FOR, or null. Set when a launch
   * surface routes to the Capabilities view because the thing the user asked
   * for looks unavailable; the view re-probes it on arrival. See
   * {@link CAPABILITY_PARAM}.
   */
  get capabilityKind(): string | null {
    return this.options?.[CAPABILITY_PARAM] ?? null;
  }

  /**
   * Clone this dock with one URL option set (or cleared with null) — the
   * generic form behind `withLang`/`withJourney` and the sticky-param
   * carry-forward in `openDock`.
   */
  withOption(key: string, value: string | null): DockPointer {
    const nextOptions = { ...(this.options ?? {}) };
    if (value) nextOptions[key] = value;
    else delete nextOptions[key];
    return new DockPointer(this.viewType, this.pointer, nextOptions, this.layout, this.page);
  }

  /** Clone this dock showing a journey, or close it (clear the param) with null. */
  withJourney(journeyId: string | null): DockPointer {
    return this.withOption(JOURNEY_PARAM, journeyId);
  }

  /** Clone this dock showing step `n` (1-based) of the running journey. */
  withJourneyStep(n: number | null): DockPointer {
    return this.withOption(JOURNEY_STEP_PARAM, n === null ? null : String(n));
  }

  /**
   * Parse dock pointer from URL segments
   * Returns null if invalid (URL validation)
   */
  static fromUrl(url: string): DockPointer;
  static fromUrl(
    viewType: string,
    pointer?: string,
    searchParams?: URLSearchParams,
    layout?: Layout,
    page?: PageId,
  ): DockPointer;
  static fromUrl(
    viewTypeOrUrl: string,
    pointer?: string,
    searchParams?: URLSearchParams,
    layout: Layout = Layout.DOCK, // Default to DOCK for backward compatibility
    page: PageId = PageId.DESK, // Default to DESK for backward compatibility
  ): DockPointer {
    if (pointer === undefined && searchParams === undefined) {
      try {
        const url = new URL(viewTypeOrUrl, 'http://flowpad.local');
        // A PATH with no layout keyword is the app root (`/`, or a base path
        // like `/agent/a/flow/f`). Gated on the leading slash so the historical
        // single-argument form — `fromUrl('editor')` — still reaches the
        // viewType parser below instead of silently resolving to the home.
        const parsedUrl = viewTypeOrUrl.startsWith('/')
          ? (parseDockUrl(url.pathname) ?? rootDockAddress(url.pathname))
          : parseDockUrl(url.pathname);
        if (parsedUrl?.viewType) {
          return DockPointer.fromUrl(
            parsedUrl.viewType,
            parsedUrl.pointer,
            url.searchParams,
            parsedUrl.layout,
            parsedUrl.page,
          );
        }
      } catch {
        // Not a URL-shaped value; continue with the historical viewType parser.
      }
    }

    const viewType = viewTypeOrUrl;

    // Validate view type only
    if (!isValidView(viewType)) {
      throw new NavigationError(NavigationErrorType.UNKNOWN_VIEW, `Invalid view type: ${viewType}`);
    }

    // `isValidView` validates at runtime but is not a type predicate, so bind
    // the narrowed value ONCE here rather than casting at each use — the
    // comparison below is against the enum and has to be enum-typed too.
    const view = viewType as ViewType;

    // Decode pointer if provided (may be URL-encoded)
    // Normalize the string "undefined" to actual undefined
    const decodedPointer = pointer && pointer !== 'undefined' ? decodeURIComponent(pointer) : undefined;

    // Parse options from query params
    const options = searchParams ? parseQueryParams(searchParams) : {};

    // A nested child view rides the END of an asset pointer (see CHILD_PARAM); lifted first so the
    // host lift and every pointer parser below see the parent's pointer alone.
    let parsedPointer = decodedPointer;
    if (view === ViewType.ASSETS || view === ViewType.PROJECT) {
      const lifted = liftChild(decodedPointer);
      if (lifted.child && lifted.section) {
        parsedPointer = lifted.pointer;
        options[CHILD_PARAM] = lifted.child;
        options[CHILD_SECTION_PARAM] = lifted.section;
      }
    }

    // The workspace host is spelled as PATH segments but carried as an option,
    // so it stays out of tab identity (see HOST_PARAM). Lifting here is what
    // keeps every downstream reader of a project sub-pointer — splitProjectPointer,
    // targetTypeId, the AssetsPage parsers — unaware that a host exists at all.
    if (view === ViewType.PROJECT) {
      const lifted = liftHostFromProjectPointer(parsedPointer);
      if (lifted.hostProcessId) {
        return new DockPointer(view, lifted.pointer, { ...options, [HOST_PARAM]: lifted.hostProcessId }, layout, page);
      }
    }

    return new DockPointer(view, parsedPointer, options, layout, page);
  }

  /**
   * Create dock pointer from ViewType (shortcut for tab docks)
   */
  static forTab(viewType: ViewType, options?: Record<string, string>, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(viewType, undefined, options || {}, layout);
  }

  /**
   * Live-session dock — /dock/live_session/<sessionId>. Top-level (not nested
   * under project/room) because the GUEST holds a session before any host
   * project or CollaborationRoom exists (DRAFT/PENDING states).
   */
  static forLiveSession(sessionId: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.LIVE_SESSION, sessionId, {}, layout);
  }

  /**
   * Automations — the list, one automation, its runs, the event bus. The place
   * rides the pointer (`runs`, `bus`); what is selected rides OPTIONS, so every
   * level is URL-addressable and reload-safe in ONE tab (`foldsPointer`). The
   * grammar lives in `automations-pointer.ts`; this only builds the pointer.
   */
  static forAutomations(route: Partial<AutomationsRoute> = {}, layout: Layout = Layout.DOCK): DockPointer {
    const full: AutomationsRoute = { place: 'list', ...route };
    return new DockPointer(ViewType.AUTOMATIONS, automationsPointer(full.place), automationsOptions(full), layout);
  }

  /**
   * Run history — `AgenticProcess` executions, the thing that actually runs.
   *
   * **On the name.** `runs` alone was un-namespaced, and `workflow_runs` is not
   * available: `workflow_run` is already a record type (Claude Code's
   * `wf_<runId>.json` mirror — see RECORD_TYPE_NAV), and docs/glossary.md bans
   * bare `Workflow*` for exactly that collision. This surface also lists runs
   * belonging to no flow at all — an ingest driver's worker, an agent launched
   * from its profile — so naming it after workflows would be a lie. It is
   * named for the entity it lists.
   *
   * URL shapes:
   *   /dock/process-runs                            every run
   *   /dock/process-runs?run=<processId>            one run, expanded
   *   /dock/process-runs?flow_id=…&node_id=…        narrowed
   *
   * Everything rides OPTIONS and the pointer stays empty — the same call
   * {@link forAutomations} makes, and for the same reason: `tabHash` folds to
   * `process-runs|`, so selecting a run is addressable and reload-safe without
   * minting a tab per run. The scope keys are the backend's `SCOPES`
   * vocabulary verbatim, so a new scope is one entry on each side.
   */
  static forProcessRuns(opts?: ProcessRunsPointerOptions, layout: Layout = Layout.DOCK): DockPointer {
    const options: Record<string, string> = {};
    if (opts?.run) options[RUN_PARAM] = opts.run;
    for (const key of PROCESS_RUN_SCOPE_KEYS) {
      const value = opts?.[key];
      if (value) options[key] = value;
    }
    return new DockPointer(ViewType.PROCESS_RUNS, undefined, options, layout);
  }

  /** The scope this runs dock is narrowed to — `{}` when it shows everything. */
  get processRunScope(): ProcessRunScope {
    const scope: ProcessRunScope = {};
    for (const key of PROCESS_RUN_SCOPE_KEYS) {
      const value = this.options?.[key];
      if (value) scope[key] = value;
    }
    return scope;
  }

  /** The run this dock has expanded, or null. */
  get selectedRunId(): string | null {
    return this.options?.[RUN_PARAM] ?? null;
  }

  /**
   * A graph workflow's canvas — and, since the studio's own state now rides
   * the URL, which panel is open and what is selected inside it.
   *
   * URL shapes:
   *   /dock/graph-workflows                                       the list
   *   /dock/graph-workflows/graph_workflow-<id>                    the canvas
   *   /dock/graph-workflows/graph_workflow-<id>?panel=runs         its history
   *   /dock/graph-workflows/graph_workflow-<id>?panel=runs&run=<runId>
   *   /dock/graph-workflows/graph_workflow-<id>?node=<nodeId>      one station
   *
   * The flow identity is the POINTER (one tab per flow); panel/run/node are
   * OPTIONS, so `tabHash` stays `graph-workflows|graph_workflow-<id>` however
   * deep you are inside it. Before this, all three lived only in the zustand
   * studio store — a flow's run was unlinkable and did not survive a reload.
   *
   * There is deliberately NO nested `…/runs` path segment: that would key a
   * second tab off the same flow, and the flow's runs are also reachable as
   * `forProcessRuns({ flow_id })` — one list under two scopes, not two lists.
   */
  static forGraphWorkflow(
    flowId?: string | null,
    sub?: { panel?: GraphWorkflowPanel; run?: string | null; node?: string | null },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    if (!flowId) return new DockPointer(ViewType.GRAPH_WORKFLOWS, undefined, undefined, layout);
    const options: Record<string, string> = {};
    if (sub?.panel) options[PANEL_PARAM] = sub.panel;
    if (sub?.run) options[RUN_PARAM] = sub.run;
    if (sub?.node) options[NODE_PARAM] = sub.node;
    return new DockPointer(
      ViewType.GRAPH_WORKFLOWS,
      new TypeId(GraphWorkflow.type, DockPointer.tryTypeId(flowId)?.id ?? flowId).toString(),
      options,
      layout,
    );
  }

  /**
   * Create dock pointer for file editor
   */
  static forFile(
    path?: string,
    options?: { line?: number; column?: number },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (options?.line !== undefined) queryOptions.line = options.line.toString();
    if (options?.column !== undefined) queryOptions.column = options.column.toString();

    return new DockPointer(ViewType.EDITOR, path, queryOptions, layout);
  }

  /**
   * Create dock pointer for checkpoint diff
   */
  static forCheckpoint(checkpointHash: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.DIFF, checkpointHash, undefined, layout);
  }

  /**
   * Create dock pointer for an asset working-tree comparison.
   */
  static forAssetCompare(payload: AssetComparePointerPayload, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(
      ViewType.DIFF,
      `${ASSET_COMPARE_POINTER_PREFIX}${encodePointerJson(payload)}`,
      undefined,
      layout,
    );
  }

  /**
   * Create dock pointer for filesystem path
   */
  static forFs(path: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.EDITOR, path, undefined, layout);
  }

  /**
   * Create dock pointer for docs viewer
   * @param filePath - Optional file path, empty string for docs list
   */
  static forDocs(filePath: string = '', layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.DOCS, filePath, undefined, layout);
  }

  /**
   * Create dock pointer for the plan viewer, addressed by the **stable PLAN
   * entity id** — the canonical, process-independent form (bookmarks).
   * Reuses the asset ref grammar: `typeid/<plan-uuid>`.
   * @param planTypeId - TypeId of the PLAN entity (`plan-<uuid>`)
   */
  static forPlan(planTypeId: TypeId, layout: Layout = Layout.DOCK): DockPointer {
    const pointer = `${AssetRoutingMethod.TYPEID}/${planTypeId.toString()}`;
    return new DockPointer(ViewType.PLAN, pointer, undefined, layout);
  }

  /**
   * Create dock pointer for the plan viewer, addressed by **VFS path** — the
   * race-free form for the live "open plan" button (no dependency on the
   * fs-records scanner having minted the PLAN entity yet). The explicit `vfs`
   * method segment means the path can never be mistaken for a TypeId, so there
   * is no embedded-`//` hazard. `vfs/<compute_node-id>/<relPath>`.
   * @param absPath - Absolute machine path to the plan .md file
   * @param computeNode - Compute node the file lives on (default: local @local)
   */
  static forPlanByPath(
    absPath: string,
    computeNode: TypeId = LOCAL_COMPUTE_NODE,
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const absVfs = VFSPath.fromMachinePath(absPath, computeNode).absVfsPath;
    const pointer = `${AssetRoutingMethod.VFS}/${absVfs}`;
    return new DockPointer(ViewType.PLAN, pointer, undefined, layout);
  }

  /**
   * Parse a plan pointer into its addressing method. Pure/sync — no network,
   * no `new TypeId` on a vfs value. Three shapes:
   *   - `typeid/<plan-uuid>`              → `{ kind: 'typeid', planTypeId }`
   *   - `vfs/<compute_node-id>/<relPath>` → `{ kind: 'vfs', vfsValue }`
   *   - `agentic_process-<id>/<path>`     → `{ kind: 'legacy', ... }` (old form;
   *     the loader resolves + redirects it to the canonical `vfs` form)
   * Returns null on anything else.
   */
  static parsePlanPointer(
    pointer: string,
  ):
    | { kind: 'typeid'; planTypeId: TypeId }
    | { kind: 'vfs'; vfsValue: string }
    | { kind: 'legacy'; agenticProcessTypeId: TypeId; filePath: string }
    | null {
    if (!pointer) return null;
    const firstSlash = pointer.indexOf('/');
    if (firstSlash < 0) return null;
    const method = pointer.slice(0, firstSlash);
    const value = pointer.slice(firstSlash + 1);
    if (!value) return null;
    // Legacy form: "agentic_process-<uuid>/<absolute-file-path-without-leading-slash>".
    // Here `method` is the whole "agentic_process-<uuid>" typeid and `value` the rel path.
    if (DockPointer.isAgenticProcessPointer(pointer)) {
      return { kind: 'legacy', agenticProcessTypeId: new TypeId(method), filePath: `/${value}` };
    }
    if (method === String(AssetRoutingMethod.TYPEID)) {
      return { kind: 'typeid', planTypeId: new TypeId(value) };
    }
    if (method === String(AssetRoutingMethod.VFS)) {
      return { kind: 'vfs', vfsValue: value };
    }
    return null;
  }

  /**
   * Create dock pointer for assistance viewer
   * @param taskId - Task ID
   */
  static forAssistance(taskTypeId?: TypeId, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.ASSISTANCE, taskTypeId?.toString(), undefined, layout);
  }

  /**
   * Create dock pointer for file explorer
   * @param path - Optional path to navigate to (file or folder)
   */
  static forExplorer(path?: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.EXPLORER, path, undefined, layout);
  }

  /**
   * Create dock pointer for the skill editor / list under the Assets browser.
   * The standalone Skills view was removed; this helper now routes into the
   * Assets editor (when a name is given) or the Assets list of skills.
   * @param skillName - Optional skill name / vfs path to open in the editor
   */
  static forSkills(skillName?: string, layout: Layout = Layout.DOCK): DockPointer {
    if (skillName) {
      return DockPointer.forAssetEditor('skill', skillName, layout);
    }
    return DockPointer.forAssetList('skill', undefined, layout);
  }

  /**
   * Create dock pointer for an asset editor.
   * Pointer format: "editor/<assetType>/<vfsPath>"
   * @param assetType - The asset type (e.g., "skill", "markdown")
   * @param vfsPath - The VFS or filesystem path to the asset
   * @param options - Query-string options (e.g. `{ editorMode: 'learning' }`)
   */
  static forAssetEditor(
    assetType: string,
    vfsPath: string,
    layout: Layout = Layout.DOCK,
    options?: Record<string, string>,
  ): DockPointer {
    // Delegates to the canonical AssetDocPointer grammar:
    //   editor/<editor>/vfs/<computeNodeTypeId>/<relPath>
    // The editor is the registry's declaration for the record type
    // (`TypeInfo.editor`; the static table only when the registry is unbound).
    // A type with no editor falls back to the editor the PATH names, never to
    // markdown: the VFS branch of AssetEditorRouter asks the backend to resolve
    // the path's type, and a markdown fallback would send a `.py` there as if
    // it were a document (FLOWPAD-2083). `editorForPath` routes unknown
    // extensions to the file-only CODE editor, which has no backing entity and
    // never resolves.
    const editor = editorForType(assetType) ?? editorForPath(vfsPath);
    const path = normalizeAssetVfsPath(vfsPath);
    return new DockPointer(
      ViewType.ASSETS,
      serializeAssetDocPointer({
        mode: AssetMode.EDITOR,
        value: path.absVfsPath,
        editor,
        method: AssetRoutingMethod.VFS,
      }),
      options,
      layout,
    );
  }

  /**
   * A webapp addressed by its own entity — `/dock/app/<artifact|micro_app>-<uuid>`.
   *
   * The counterpart to `forApp`, which is the skill-UI dock (`ViewType.APPS`) and
   * a different thing entirely. Extra options (e.g. `source`) become the app's
   * query string, so an app is always told what to act on through its URL.
   */
  static forAppEntity(typeId: TypeId, options?: Record<string, string>, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.APP, typeId.toString(), options, layout);
  }

  /**
   * Create dock pointer for an entity-backed asset by its stable TypeId — the
   * preferred, relocation-proof form. The loader resolves it by id (no path
   * discovery), so navigation commits instantly.
   * Pointer format: "editor/<editor>/typeid/<type>-<uuid>"
   */
  static forAssetEditorByTypeId(
    assetType: string,
    typeId: TypeId,
    layout: Layout = Layout.DOCK,
    options?: Record<string, string>,
    editorOverride?: AssetEditor,
  ): DockPointer {
    const editor = editorOverride ?? editorForType(assetType) ?? AssetEditor.MARKDOWN;
    return new DockPointer(
      ViewType.ASSETS,
      serializeAssetDocPointer({
        mode: AssetMode.EDITOR,
        value: typeId.toString(),
        editor,
        method: AssetRoutingMethod.TYPEID,
      }),
      options,
      layout,
    );
  }

  /**
   * Create dock pointer for a wiki link by name. Resolves to a markdown record
   * at view time; the URL stays at the name form (rename-resilient).
   * Pointer format: "wiki/<encoded name>"
   * URL: /dock/assets/wiki/<encoded name>
   */
  static forWiki(name: string, layout: Layout = Layout.DOCK, space?: string, fragment?: string): DockPointer {
    // Canonical grammar: wiki/<space>/<name> (space default @local). An optional
    // `fragment` deep-links to a heading; it rides as a query param, not the path.
    const options = fragment ? { wikiFragment: fragment } : undefined;
    return new DockPointer(
      ViewType.ASSETS,
      serializeAssetDocPointer({
        mode: AssetMode.WIKI,
        value: assetWikiValue(name, space),
      }),
      options,
      layout,
    );
  }

  /**
   * Create dock pointer for an asset list filtered to a specific type.
   * Pointer format: "list/<typeName>"
   * URL: /dock/assets/list/<typeName>
   */
  static forAssetList(
    typeName: string = 'all',
    options?: { scope?: ScopeFilter },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const base = new DockPointer(ViewType.ASSETS, `list/${typeName}`, undefined, layout);
    return options?.scope ? base.withScopeFilter(options.scope) : base;
  }

  /**
   * Create a pointer for the project landing rendered inside the project-scoped
   * Assets tab. Assets is scope-keyed, so this shares the same tab as the asset
   * manager for that project while still giving the landing a restorable URL.
   */
  static forAssetProjectHome(options?: { scope?: ScopeFilter }, layout: Layout = Layout.DOCK): DockPointer {
    const base = new DockPointer(ViewType.ASSETS, AssetMode.PROJECT_HOME, undefined, layout);
    return options?.scope ? base.withScopeFilter(options.scope) : base;
  }

  /** The project's setup page, rebased onto `/dock/project/<projectId>/project-setup`. */
  static forProjectSetup(projectId: string, layout: Layout = Layout.DOCK): DockPointer {
    return DockPointer.rebaseAssetsOntoProject(
      new DockPointer(ViewType.ASSETS, AssetMode.PROJECT_SETUP, undefined, layout),
      projectId,
    );
  }

  /**
   * Create dock pointer for an asset folder view (filtered list under a folder).
   * Pointer format: "folder/<typeName>/<typeid>/<relPath>"
   *   - typeid is a VFS entity identifier like "compute_node-@local" or "project-<uuid>".
   *   - relPath is the folder path relative to the typeid (may be empty for the vault root).
   * URL: /dock/assets/folder/<typeName>/<typeid>/<relPath>
   */
  static forAssetFolder(
    typeName: string,
    typeid: string,
    relPath: string = '',
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const cleanRel = relPath.replace(/^\/+/, '').replace(/\/+$/, '');
    const pointer = cleanRel ? `folder/${typeName}/${typeid}/${cleanRel}` : `folder/${typeName}/${typeid}`;
    return new DockPointer(ViewType.ASSETS, pointer, undefined, layout);
  }

  /**
   * Create a filesystem-browser pointer from canonical VFS identity.
   *
   * Route grammar: `fs/vfs/<absVfsPath>`. `vfs://` is deliberately not placed
   * in the path segments; `VFSPath.absVfsPath` is the route-safe serialization.
   */
  static forAssetFs(vfsPath: VFSPath, layout: Layout = Layout.DOCK, select?: string): DockPointer {
    if (!vfsPath.isAbsolute) {
      throw new Error(`Asset filesystem pointers require an absolute VFS path: "${vfsPath.rawPath}"`);
    }
    return new DockPointer(
      ViewType.ASSETS,
      `fs/${AssetRoutingMethod.VFS}/${vfsPath.absVfsPath}`,
      select ? { [FS_SELECT_PARAM]: select } : undefined,
      layout,
    );
  }

  /**
   * Transitional source-compatible builder. New callers should construct a
   * VFSPath explicitly and use `forAssetFs`; relative and machine paths here
   * resolve against the canonical local locator (`compute_node-@local`).
   *
   * @deprecated use `forAssetFs(VFSPath)`
   */
  static forAssetFsFolder(path: string, layout: Layout = Layout.DOCK): DockPointer {
    const parsed = VFSPath.parse(path);
    const vfsPath = parsed.isAbsolute
      ? parsed
      : path.startsWith('/') || /^[A-Za-z]:[/\\]/.test(path)
        ? VFSPath.fromMachinePath(path, LOCAL_COMPUTE_NODE)
        : VFSPath.fromTypeId(LOCAL_COMPUTE_NODE, normalizeRel(path));
    return DockPointer.forAssetFs(vfsPath, layout);
  }

  /** Parse the canonical `fs/vfs/<absVfsPath>` assets route. */
  static parseAssetFsPointer(pointer: string | undefined | null): VFSPath | null {
    const prefix = `fs/${AssetRoutingMethod.VFS}/`;
    if (!pointer?.startsWith(prefix)) return null;
    const parsed = VFSPath.parse(pointer.slice(prefix.length));
    return parsed.isAbsolute ? parsed : null;
  }

  /**
   * Return the canonical replacement for a legacy `fs/<relative>` route.
   * Legacy fs routes could only address this machine, so the durable locator is
   * `compute_node-@local`; live UUIDs remain an I/O concern.
   */
  canonicalLegacyAssetFsDock(): DockPointer | null {
    const assetsPointer = this.viewType === ViewType.ASSETS ? (this.pointer ?? null) : this.assetSubPointer;
    const canonicalPrefix = `fs/${AssetRoutingMethod.VFS}/`;
    if (!assetsPointer?.startsWith('fs/') || assetsPointer.startsWith(canonicalPrefix)) {
      return null;
    }

    const relativePath = normalizeRel(assetsPointer.slice('fs/'.length));
    const canonical = DockPointer.forAssetFs(VFSPath.fromTypeId(LOCAL_COMPUTE_NODE, relativePath), this.layout);
    const rebased =
      this.viewType === ViewType.PROJECT
        ? DockPointer.rebaseAssetsOntoProject(canonical, DockPointer.splitProjectPointer(this.pointer).projectId)
        : canonical;
    return new DockPointer(rebased.viewType, rebased.pointer, this.options, this.layout, this.page);
  }

  /**
   * Parse a folder pointer into its parts.
   * Returns null if the pointer is not a folder pointer.
   */
  static parseAssetFolderPointer(
    pointer: string | undefined,
  ): { typeName: string; typeid: string; relPath: string } | null {
    if (!pointer || !pointer.startsWith('folder/')) return null;
    const rest = pointer.slice('folder/'.length);
    const firstSlash = rest.indexOf('/');
    if (firstSlash < 0) return null;
    const typeName = rest.slice(0, firstSlash);
    const afterType = rest.slice(firstSlash + 1);
    const secondSlash = afterType.indexOf('/');
    if (secondSlash < 0) {
      // no relPath — pointer addresses the vault root itself
      return { typeName, typeid: afterType, relPath: '' };
    }
    const typeid = afterType.slice(0, secondSlash);
    const relPath = afterType.slice(secondSlash + 1);
    return { typeName, typeid, relPath };
  }

  /**
   * Create dock pointer for a project's collaboration view, optionally with
   * an active collaboration_room and/or an active tab inside that room, or
   * a focused conversation.
   *
   * URL formats:
   *   /dock/project/<projectId>
   *   /dock/project/<projectId>/collaboration_room/<roomId>
   *   /dock/project/<projectId>/collaboration_room/<roomId>/tab/<typeid>
   *   /dock/project/<projectId>/collaboration_room/<roomId>/session/<sessionId>
   *   /dock/project/<projectId>/conversation/<conversationId>
   *
   * `typeid` is the standard TypeId string (e.g. "agentic_process-<uuid>").
   *
   * Precedence: when both `roomId` and `conversationId` are passed, `conversationId`
   * wins — the room shape is dropped to keep the URL unambiguous. Within a room,
   * `sessionId` (the active shared session) takes precedence over `tab`.
   */
  static forProject(
    projectId?: string,
    sub?: { roomId?: string | null; tab?: TypeId | null; conversationId?: string | null; sessionId?: string | null },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    if (!projectId) return new DockPointer(ViewType.PROJECT, undefined, undefined, layout);
    const segments: string[] = [projectId];
    if (sub?.conversationId) {
      segments.push('conversation', sub.conversationId);
    } else if (sub?.roomId) {
      segments.push('collaboration_room', sub.roomId);
      if (sub.sessionId) {
        segments.push('session', sub.sessionId);
      } else if (sub.tab) {
        segments.push('tab', sub.tab.toString());
      }
    }
    return new DockPointer(ViewType.PROJECT, segments.join('/'), undefined, layout);
  }

  /**
   * The helpdesk portal for `projectId`, optionally showing one guide.
   *
   * URL shapes:
   *   /dock/helpdesk/<projectId>
   *   /dock/helpdesk/<projectId>/article/<repo-relative path>
   *
   * `articlePath` keeps its slashes — `buildDockUrl` encodes segments
   * individually, and `parseHelpdeskPointer` re-joins the tail, so
   * `docs/Getting Started/Welcome.md` survives the round trip intact.
   */
  static forHelpdesk(projectId?: string, articlePath?: string | null, layout: Layout = Layout.DOCK): DockPointer {
    if (!projectId) return new DockPointer(ViewType.HELPDESK, undefined, undefined, layout);
    const pointer = articlePath ? `${projectId}/article/${articlePath}` : projectId;
    return new DockPointer(ViewType.HELPDESK, pointer, undefined, layout);
  }

  /**
   * Parse a helpdesk pointer. Returns nulls for anything absent or malformed —
   * never throws, so a hand-edited URL renders the portal root rather than
   * blowing up the shell.
   */
  static parseHelpdeskPointer(pointer?: string | null): {
    projectId: string | null;
    articlePath: string | null;
  } {
    const segments = (pointer ?? '').split('/').filter(Boolean);
    if (segments.length === 0) return { projectId: null, articlePath: null };
    const [projectId, marker, ...rest] = segments;
    const articlePath = marker === 'article' && rest.length ? rest.join('/') : null;
    return { projectId, articlePath };
  }

  /**
   * Parse a project pointer string.
   *
   * Accepted shapes:
   *   <projectId>
   *   <projectId>/collaboration_room/<roomId>
   *   <projectId>/collaboration_room/<roomId>/tab/<type>-<id>
   *   <projectId>/collaboration_room/<roomId>/session/<sessionId>
   *   <projectId>/conversation/<conversationId>
   *
   * Returns nulls for segments that aren't present or the input is malformed.
   */
  static parseProjectPointer(pointer: string | undefined | null): {
    projectTypeId: TypeId | null;
    roomId: string | null;
    tabTypeId: TypeId | null;
    sessionId: string | null;
    conversationId: string | null;
  } {
    if (!pointer) return { projectTypeId: null, roomId: null, tabTypeId: null, sessionId: null, conversationId: null };
    const parts = pointer.split('/').filter(Boolean);
    // parts[0] identifies the project. It may arrive bare (`<id>`) or as a
    // serialized `<type>-<id>` typeid — route it through TypeId so the type
    // token is parsed by the one object that owns that grammar, never
    // string-matched / prefix-stripped here.
    const projectTypeId = parts[0] ? DockPointer.projectSegmentToTypeId(parts[0]) : null;
    let roomId: string | null = null;
    let tabTypeId: TypeId | null = null;
    let sessionId: string | null = null;
    let conversationId: string | null = null;
    if (parts[1] === 'conversation' && parts[2]) {
      conversationId = parts[2];
    } else if (parts[1] === 'collaboration_room' && parts[2]) {
      roomId = parts[2];
      if (parts[3] === 'session' && parts[4]) {
        sessionId = parts[4];
      } else if (parts[3] === 'tab' && parts[4]) {
        try {
          tabTypeId = new TypeId(parts[4]);
        } catch {
          tabTypeId = null;
        }
      }
    }
    return { projectTypeId, roomId, tabTypeId, sessionId, conversationId };
  }

  /**
   * Construct a TypeId, or return null instead of throwing — the shared
   * non-throwing coercion used by the pointer parsers (`targetTypeId`,
   * `projectSegmentToTypeId`) that turn a `<type>-<id>`-or-bare-id segment into
   * a TypeId.
   */
  private static tryTypeId(type: string, id?: string): TypeId | null {
    try {
      return id !== undefined ? new TypeId(type, id) : new TypeId(type);
    } catch {
      return null;
    }
  }

  /**
   * Coerce a project-pointer segment into a `project` TypeId. The segment is a
   * serialized `<type>-<id>` (e.g. `project-<uuid>`) or a bare id; TypeId parses
   * the type token when present, and the project view supplies the type for a
   * bare id. The grammar lives entirely in TypeId — no literal prefixing here.
   */
  private static projectSegmentToTypeId(segment: string): TypeId | null {
    return DockPointer.tryTypeId(Project.type, DockPointer.tryTypeId(segment)?.id ?? segment);
  }

  /**
   * Split a project pointer into `{ projectId, assetSubPointer }`.
   *
   * The sub-pointer is the same shape AssetsPage already accepts at
   * `/dock/assets/<sub>` (e.g. `editor/<typeid>`, `list/<typeName>`,
   * `folder/<typeid>/<relPath>`, `wiki/<name>`) — so the project view can
   * reuse AssetsPage's existing selection parser without inventing new shapes.
   */
  static splitProjectPointer(pointer: string | undefined | null): {
    projectId: string | null;
    assetSubPointer: string;
  } {
    if (!pointer) return { projectId: null, assetSubPointer: '' };
    const slash = pointer.indexOf('/');
    if (slash < 0) return { projectId: pointer, assetSubPointer: '' };
    const assetSubPointer = pointer.slice(slash + 1);
    // Tripwire. The host is lifted out of the pointer in `fromUrl` and only put
    // back when serializing, so a `process/` prefix here means a stored pointer
    // or a hand-built dock kept the composite form. Left alone it degrades
    // silently — `AssetDocPointer.parse` throws `unknown mode "process"`,
    // `loadAssetRoute` swallows it with a console.warn, and the user gets a
    // blank pane. Fail loudly at the one chokepoint every consumer shares.
    if (assetSubPointer.startsWith(`${HOST_SEGMENT}/`)) {
      // Loud, but NOT fatal: this is a read path — three components call it
      // during render (AssetsPage, useAssetsModel, HubProjectPage) and so does
      // `isAdoptableChildDock` on the navigation path. Throwing would upgrade a
      // degraded pane into a dead screen. Strip and carry on; the write
      // boundary (`fromUrl`/`withHost`) is where a composite is refused.
      console.error(
        `[DockPointer] project pointer still carries its workspace host: ${pointer}. ` +
          'Build it through DockPointer.fromUrl/withHost so the host rides in options.',
      );
      return {
        projectId: pointer.slice(0, slash),
        assetSubPointer: liftHostFromProjectPointer(pointer).pointer?.slice(slash + 1) ?? '',
      };
    }
    return {
      projectId: pointer.slice(0, slash),
      assetSubPointer,
    };
  }

  /** The URL is pointer identity, so each page has its own durable tab. */
  static forWebUrl(url: string): DockPointer {
    return new DockPointer(ViewType.WEB_APP, pointerForWebUrl(url));
  }

  get webUrl(): string | null {
    return this.viewType === ViewType.WEB_APP ? webUrlFromPointer(this.pointer) : null;
  }

  /**
   * Rebase a `ViewType.ASSETS` pointer onto `/dock/project/<projectId>` so
   * navigation initiated by an assets-shaped builder (`forAssetEditor`,
   * `forAssetFolder`, `forAssetList`, `forAssetWiki`) stays inside the project
   * shell. Non-ASSETS pointers and falsy `projectId` pass through unchanged —
   * call sites can use this unconditionally.
   */
  static rebaseAssetsOntoProject(p: DockPointer, projectId: string | null | undefined): DockPointer {
    if (!projectId || p.viewType !== ViewType.ASSETS) return p;
    const sub = p.pointer ? `${projectId}/${p.pointer}` : projectId;
    return new DockPointer(ViewType.PROJECT, sub, p.options, p.layout, p.page);
  }

  /**
   * Create dock pointer for the stream inbox, optionally focused on a specific conversation
   * and/or message via query params.
   *
   * URL formats:
   *   /dock/stream_inbox
   *   /dock/stream_inbox?conversation=<id>
   *   /dock/stream_inbox?conversation=<id>&message=<id>
   */
  static forStreamInbox(
    options?: { conversationId?: string | null; messageId?: string | null },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (options?.conversationId) queryOptions.conversation = options.conversationId;
    if (options?.messageId) queryOptions.message = options.messageId;
    return new DockPointer(
      ViewType.STREAM_INBOX,
      undefined,
      Object.keys(queryOptions).length ? queryOptions : undefined,
      layout,
    );
  }

  /** The one address for an Agent's stream inbox surface. `channel` (a `channelKeyOf` key) opens it
   *  narrowed to that channel — a query option, like `forStreamInbox`'s; the path grammar is unchanged.
   *
   * URL formats:
   *   /dock/agent/<id>/stream_inbox
   *   /dock/agent/<id>/stream_inbox?channel=<key>
   */
  static forAgentStreamInbox(
    agentId: string,
    options?: { channel?: string | null },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions = options?.channel ? { channel: options.channel } : undefined;
    return new DockPointer(ViewType.AGENT, `${agentId}/stream_inbox`, queryOptions, layout);
  }

  static parseAgentPointer(pointer: string | undefined | null): {
    agentId: string | null;
    view: 'stream_inbox' | null;
  } {
    const parts = pointer?.split('/').filter(Boolean) ?? [];
    return parts.length === 2 && parts[0] && parts[1] === 'stream_inbox'
      ? { agentId: parts[0], view: 'stream_inbox' }
      : { agentId: null, view: null };
  }

  /** A SESSION's own dock — the shell dock of `agentic_process-<id>`. Pair with
   *  `withViewMode` for a surface (Vibe maps it to the Vibe host dock). */
  static forSession(processId: string): DockPointer {
    return new DockPointer(ViewType.SHELL, `${AgenticProcess.type}${TypeId.DELIMITER}${processId}`);
  }

  /**
   * Create dock pointer for shell/terminal viewer
   * @param sessionId - Optional shell session ID (e.g., 'run', 'flowShell', or custom UUID)
   * @param options.cwd - Working directory to cd into before starting the shell
   * @param options.startCommand - Command typed AND submitted once the shell is ready
   * @param options.skipPermissions - Pass through `--dangerously-skip-permissions` semantics where applicable
   */
  static forShell(
    sessionId?: string,
    options?: { cwd?: string; startCommand?: string; skipPermissions?: boolean },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (options?.cwd) queryOptions.cwd = options.cwd;
    if (options?.startCommand) queryOptions[START_COMMAND_PARAM] = options.startCommand;
    if (options?.skipPermissions) queryOptions.skipPermissions = 'true';
    return new DockPointer(ViewType.SHELL, sessionId, queryOptions, layout);
  }

  /**
   * The command this shell dock asks its terminal to run on first attach.
   * Consumed ONCE by the mounted `InteractiveTerminal`
   * (see `START_COMMAND_PARAM`) — a PTY write is a side effect on a live
   * terminal, so it belongs to the mounted view, not to the loader or to the
   * click handler that navigated here.
   */
  get shellStartCommand(): string | null {
    return this.options?.[START_COMMAND_PARAM] || null;
  }

  /**
   * The bare Shell id this dock addresses, or null when it addresses something
   * else (a process, nothing at all).
   *
   * A shell has TWO spellings and they are not interchangeable: `Shell.dockPointer`
   * is the TypeId (`shell-<uuid>`) while every consumer holding a "session id"
   * — tabs, the PTY transport, `InteractiveTerminal` — holds the bare uuid.
   * Comparing the two raw silently never matches, which is exactly how the
   * typed-command effect came to do nothing at all. One accessor, so the next
   * comparison cannot get it wrong either.
   */
  get shellId(): string | null {
    const pointer = this.viewType === ViewType.SHELL ? this.pointer : null;
    if (!pointer || DockPointer.isAgenticProcessPointer(pointer)) return null;
    return pointer.startsWith(`${SHELL_TYPE}-`) ? pointer.slice(SHELL_TYPE.length + 1) : pointer;
  }

  /** Clone this dock with the command param dropped — what the terminal
   *  navigates to after typing, so a refresh cannot retype the command. */
  withoutShellStartCommand(): DockPointer {
    return this.withOption(START_COMMAND_PARAM, null);
  }

  /**
   * THE app root — the desk home, spelled `/`.
   *
   * An ordinary location: the desk `HOME` view with no pointer. It used to be
   * the ABSENCE of a pointer (`currentDock === null`), which left every caller
   * that had to work "dock or home" holding a raw URL string. Compose on it like
   * any other pointer: `DockPointer.root().withJourney(id)`.
   *
   * Not a tab — HOME is `chrome: 'fullbleed'`, so `tabHash` is null.
   */
  static root(): DockPointer {
    return new DockPointer(ViewType.HOME);
  }

  /** True when this pointer IS the app root (see `isRootAddress`). */
  get isRoot(): boolean {
    return isRootAddress(this.viewType, this.pointer, this.layout, this.page);
  }

  /** This pointer carrying the query options of `url`. The root has no path of
   *  its own to parse, so its options have to be lifted across explicitly. */
  withOptionsFromUrl(url: string): DockPointer {
    const query = new URL(url, 'http://flowpad.local').searchParams;
    return new DockPointer(this.viewType, this.pointer, parseQueryParams(query), this.layout, this.page);
  }

  /**
   * Create dock pointer for HOME/LiveStatus view with optional tab and item
   * URL structure: /dock/home/<tab>?item=<item>&scope=<scope>&project=<project>
   *
   * @param tab - Tab name (e.g., "summary", "projects", "sessions")
   * @param item - Optional item within the tab
   * @param options - Optional scope filter options
   * @param options.scope - Scope filter: 'all' | 'global' | 'project'
   * @param options.project - Project encoded name (when scope is 'project')
   */
  static forHome(
    tab?: string,
    item?: string,
    options?: { scope?: string; project?: string; expand?: boolean; vibeNoProcess?: boolean },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (item) queryOptions.item = item;
    if (options?.scope && options.scope !== 'all') queryOptions.scope = options.scope;
    if (options?.project) queryOptions.project = options.project;
    if (options?.expand) queryOptions.expand = 'true';
    if (options?.vibeNoProcess) queryOptions.vibeNoProcess = 'true';
    return new DockPointer(ViewType.HOME, tab, queryOptions, layout);
  }

  /**
   * Create dock pointer for System Profile view with optional tab
   * URL structure: /dock/system_profile/<tab>?item=<item>&scope=<scope>&project=<project>
   *
   * @param tab - Tab name (e.g., "summary", "projects", "sessions", "transcripts")
   * @param item - Optional item within the tab
   * @param options - Optional scope filter options
   * @param options.scope - Scope filter: 'all' | 'global' | 'project'
   * @param options.project - Project encoded name (when scope is 'project')
   */
  static forSystemProfile(
    tab?: string,
    item?: string,
    options?: { scope?: string; project?: string },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (item) queryOptions.item = item;
    if (options?.scope && options.scope !== 'all') queryOptions.scope = options.scope;
    if (options?.project) queryOptions.project = options.project;
    return new DockPointer(ViewType.SYSTEM_PROFILE, tab, queryOptions, layout);
  }

  /**
   * Create a DockPointer for showing MCP UI components
   *
   * URI format: ui://<entity_vfs>?page=<page>&component=<component>
   * - entity_vfs: TypeId or skill name (e.g., "agent-@my-agent" or "onboarding")
   * - page: Page name (defaults to "index" if not provided)
   * - component: Component name (defaults to "main" if not provided)
   *
   * @param entityVfs - Entity VFS path or skill name
   * @param page - Page name within the entity (default: "index")
   * @param component - Component name within the page (default: "main")
   */
  static forShow(entityVfs: string, page?: string, component?: string, layout: Layout = Layout.DOCK): DockPointer {
    const queryOptions: Record<string, string> = {};
    // Store page and component in query params - apply defaults later in ShowView
    if (page) queryOptions.page = page;
    if (component) queryOptions.component = component;
    return new DockPointer(ViewType.SHOW, entityVfs, queryOptions, layout);
  }

  /**
   * Create a DockPointer for a named app (skill UI). Mounts at /dock/apps/<uname>/<routerPath>.
   * The pointer encodes "<uname>/<routerPath>"; the host splits at the first slash.
   * `routerPath` is opaque to the host — interpreted by the app itself.
   */
  static forApp(
    uname: string,
    routerPath: string = '',
    options?: Record<string, string>,
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const pointer = routerPath ? `${uname}/${routerPath}` : uname;
    return new DockPointer(ViewType.APPS, pointer, options, layout);
  }

  /** Split an APPS pointer into its `{ uname, routerPath }` parts. */
  static parseAppPointer(pointer: string | undefined): { uname: string; routerPath: string } | null {
    if (!pointer) return null;
    const idx = pointer.indexOf('/');
    if (idx < 0) return { uname: pointer, routerPath: '' };
    return { uname: pointer.slice(0, idx), routerPath: pointer.slice(idx + 1) };
  }

  /**
   * Create a DockPointer for the built-in dep-graph viewer at /dock/graph/<type>/<id>.
   * Pointer encodes "<type>/<id>"; the viewer focuses on that entity (local-mode root).
   * Omit the typeId to open the full graph with no focus.
   * Options support `depth` (1-3) and `selected` (a node key to highlight).
   */
  static forGraph(
    typeId?: TypeId | null,
    options?: { depth?: number; selected?: string; hidden?: readonly string[]; query?: string },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const pointer = typeId ? `${typeId.type}/${typeId.id}` : undefined;
    const queryOptions: Record<string, string> = {};
    if (options?.depth) queryOptions.depth = String(options.depth);
    if (options?.selected) queryOptions.selected = options.selected;
    const hidden = [...new Set(options?.hidden ?? [])].filter(Boolean).sort();
    if (hidden.length) queryOptions.hide = hidden.join(',');
    if (options?.query) queryOptions.q = options.query;
    return new DockPointer(
      ViewType.GRAPH,
      pointer,
      Object.keys(queryOptions).length ? queryOptions : undefined,
      layout,
    );
  }

  /** Create one projection-first WorldView URL with all in-view state in query options. */
  static forWorldView(
    projection: WorldViewProjectionName = WorldViewProjection.DEPLOYMENT,
    options?: {
      focus?: TypeId | string | null;
      depth?: number;
      selected?: string;
      signal?: WorldViewColorMode;
      /** Renderer choice — serialized as `?render=` (see GraphPresentation). */
      render?: GraphPresentation;
      hidden?: readonly string[];
      query?: string;
    },
    layout: Layout = Layout.DOCK,
    page: PageId = projection === WorldViewProjection.DEPLOYMENT ? PageId.DESK : PageId.HUB,
  ): DockPointer {
    const queryOptions: Record<string, string> = {};
    const focus = typeof options?.focus === 'string' ? options.focus : options?.focus?.toString();
    if (focus) queryOptions.focus = focus;
    if (options?.depth) queryOptions.depth = String(options.depth);
    if (options?.selected) queryOptions.selected = options.selected;
    if (
      projection === WorldViewProjection.DEPLOYMENT &&
      options?.signal &&
      options.signal !== DEFAULT_WORLDVIEW_COLOR_MODE
    ) {
      queryOptions.signal = options.signal;
    }
    if (options?.render && options.render !== DEFAULT_GRAPH_PRESENTATION) queryOptions.render = options.render;
    const hidden = [...new Set(options?.hidden ?? [])].filter(Boolean).sort();
    if (hidden.length) queryOptions.hide = hidden.join(',');
    if (options?.query) queryOptions.q = options.query;
    return new DockPointer(
      ViewType.WORLDVIEW,
      projection,
      Object.keys(queryOptions).length ? queryOptions : undefined,
      layout,
      page,
    );
  }

  /** Shared query-option assembly for the subgraph-surface pointers. */
  private static subgraphOptions(options?: {
    depth?: number;
    /** Omit `depth` when it equals this (URL hygiene, one rule for every
     *  subgraph-surface pointer). */
    defaultDepth?: number;
    selected?: string;
    render?: GraphPresentation;
    hidden?: readonly string[];
    query?: string;
    carry?: Record<string, string>;
  }): Record<string, string> | undefined {
    const queryOptions: Record<string, string> = { ...(options?.carry ?? {}) };
    if (options?.render && options.render !== DEFAULT_GRAPH_PRESENTATION) queryOptions.render = options.render;
    if (options?.depth && options.depth !== options.defaultDepth) queryOptions.depth = String(options.depth);
    if (options?.selected) queryOptions.selected = options.selected;
    const hidden = [...new Set(options?.hidden ?? [])].filter(Boolean).sort();
    if (hidden.length) queryOptions.hide = hidden.join(',');
    if (options?.query) queryOptions.q = options.query;
    return Object.keys(queryOptions).length ? queryOptions : undefined;
  }

  /**
   * Tag graph/tree at `/dock/tag/graph[/<name>]` — <name> is a dot-path
   * TAG NAME (not a typeid; ghost tags are first-class). Focus lives in
   * the pointer (dependency pattern); `view=tree` and friends ride options.
   */
  static forTagGraph(
    tag?: string | null,
    options?: {
      /** Data shape owned by this surface (`?view=tree` = ontology tree). */
      view?: 'tree';
      depth?: number;
      /** Omit `depth` when it equals this — forwarded to `subgraphOptions`. */
      defaultDepth?: number;
      selected?: string;
      render?: GraphPresentation;
      hidden?: readonly string[];
      query?: string;
      carry?: Record<string, string>;
    },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const pointer = tag ? `graph/${encodeURIComponent(tag)}` : 'graph';
    const carry = { ...(options?.carry ?? {}) };
    if (options?.view) carry.view = options.view;
    return new DockPointer(ViewType.TAG, pointer, DockPointer.subgraphOptions({ ...options, carry }), layout);
  }

  /** Split a TAG pointer: `graph[/<name>]` → `{ sub: 'graph', tag }`. */
  static parseTagPointer(pointer: string | undefined): { sub: string; tag: string | null } | null {
    if (!pointer) return null;
    const idx = pointer.indexOf('/');
    const sub = idx < 0 ? pointer : pointer.slice(0, idx);
    if (sub !== 'graph') return null;
    const tag = idx < 0 ? null : pointer.slice(idx + 1);
    return { sub, tag: tag ? DockPointer.tryDecode(tag) : null };
  }

  /** Best-effort decode for pointer segments encoded by the `for*` factories. */
  private static tryDecode(segment: string): string {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  }

  /**
   * Generic entity-subgraph at `/dock/subgraph/<projection>[/<focusKey>]` —
   * layer 2's zero-new-frontend-code path. The focus segment is a node key.
   */
  static forSubgraph(
    projection: string,
    focusKey?: string | null,
    options?: {
      depth?: number;
      /** Omit `depth` when it equals this — forwarded to `subgraphOptions`. */
      defaultDepth?: number;
      selected?: string;
      render?: GraphPresentation;
      hidden?: readonly string[];
      query?: string;
      carry?: Record<string, string>;
    },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const pointer = focusKey ? `${projection}/${encodeURIComponent(focusKey)}` : projection;
    return new DockPointer(ViewType.SUBGRAPH, pointer, DockPointer.subgraphOptions(options), layout);
  }

  /** Split a SUBGRAPH pointer into `{ projection, focus }`. */
  static parseSubgraphPointer(pointer: string | undefined): { projection: string; focus: string | null } | null {
    if (!pointer) return null;
    const idx = pointer.indexOf('/');
    if (idx < 0) return { projection: pointer, focus: null };
    const projection = pointer.slice(0, idx);
    if (!projection) return null;
    const focus = pointer.slice(idx + 1);
    return { projection, focus: focus ? DockPointer.tryDecode(focus) : null };
  }

  /**
   * Create a DockPointer for the frozen-context viewer at
   * `/dock/graph_context/<id>`. `id` is the GraphContext entity's UUID.
   */
  static forGraphContext(id: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.GRAPH_CONTEXT, id, undefined, layout);
  }

  /**
   * Create a DockPointer for the diagnosis viewer at `/dock/diagnosis/<id>`.
   * `id` is the FlowpadDiagnosis entity's UUID.
   */
  static forDiagnosis(id: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.DIAGNOSIS, id, undefined, layout);
  }

  /** Split a GRAPH pointer into its `{ type, id }` parts. */
  static parseGraphPointer(pointer: string | undefined): { type: string; id: string } | null {
    if (!pointer) return null;
    const parts = pointer.split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
    return { type: parts[0], id: parts[1] };
  }

  /** Parse one of the three canonical projection pointer values. */
  static parseWorldViewProjection(pointer: string | undefined): WorldViewProjectionName | null {
    return isWorldViewProjection(pointer) ? pointer : null;
  }

  /** Split a retired entity-rooted WORLDVIEW pointer for redirect compatibility. */
  static parseWorldViewPointer(pointer: string | undefined): { type: string; id: string } | null {
    return DockPointer.parseGraphPointer(pointer);
  }

  /**
   * Create a DockPointer for the docs knowledge browser at
   * `/dock/k-browser/<method>/<value>`. Mirrors the editor addressing grammar:
   * the explicit `<method>` segment (`vfs` | `typeid`) keeps a filesystem path
   * from ever being parsed as a TypeId. `value` is the docs-root vfs path (vfs)
   * or a `<type>-<id>` TypeId (typeid).
   */
  static forKnowledgeBrowser(
    value: string,
    method: 'vfs' | 'typeid' = 'vfs',
    options?: { selected?: string },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    // vfs paths are absolute — strip the leading "/" so the method<->path
    // delimiter isn't an embedded "//" in the URL (react-router normalizes
    // "//" to "/", silently demoting the path to a relative one). The parser
    // re-adds it. Same hazard + fix as forPlan above.
    const cleanValue = method === 'vfs' && value.startsWith('/') ? value.slice(1) : value;
    const pointer = `${method}/${cleanValue}`;
    const queryOptions: Record<string, string> = {};
    if (options?.selected) queryOptions.selected = options.selected;
    return new DockPointer(
      ViewType.K_BROWSER,
      pointer,
      Object.keys(queryOptions).length ? queryOptions : undefined,
      layout,
    );
  }

  /** Split a K_BROWSER pointer into `{ method, value }` (default method `vfs`).
   *  vfs values get their leading "/" re-added (forKnowledgeBrowser strips it);
   *  legacy double-slash URLs (`vfs//Users/…`) parse identically. */
  static parseKnowledgeBrowserPointer(pointer: string | undefined): { method: 'vfs' | 'typeid'; value: string } | null {
    if (!pointer) return null;
    const idx = pointer.indexOf('/');
    if (idx < 0) return { method: 'vfs', value: pointer };
    const method = pointer.slice(0, idx) === 'typeid' ? 'typeid' : 'vfs';
    let value = pointer.slice(idx + 1);
    if (method === 'vfs' && value && !value.startsWith('/') && !/^[A-Za-z]:[/\\]/.test(value)) {
      value = `/${value}`;
    }
    return { method, value };
  }

  /**
   * Parse a lens pointer into its parts
   * @param pointer - The full pointer string (e.g., "claude/transcript/abc123")
   */
  static parseLensPointer(pointer: string): LensPointerParts | null {
    const parts = pointer.split('/');
    // Two-segment lenses (category/type, no ref) are valid — e.g.
    // `fs-records/scan`, `fs-records/llm-indexers`, `cli/log`, `claude/context`.
    // Three-or-more-segment lenses additionally carry a ref (e.g.
    // `claude/transcript/<sessionId>`).
    if (parts.length < 2) return null;
    return {
      category: parts[0],
      type: parts[1],
      ref: parts.slice(2).join('/'), // '' when absent; may contain slashes
    };
  }

  /**
   * Create dock pointer for lens viewer
   * @param category - Lens category (e.g., "claude")
   * @param type - Lens type (e.g., "transcript")
   * @param ref - Reference (e.g., encoded vfs path)
   */
  static forLens(
    category: string,
    type: string,
    ref: string,
    layout: Layout = Layout.DOCK,
    options?: Record<string, string>,
  ): DockPointer {
    const pointer = `${category}/${type}/${ref}`;
    return new DockPointer(ViewType.LENS, pointer, options, layout);
  }

  /**
   * Create dock pointer for tasks view.
   * @param taskId - Optional task ID to view/edit
   * @param options.conversationId - Optional conversation id to canonicalise
   *   into the URL — produces `/dock/tasks/<taskId>/conversation/<convId>`.
   *   The task view itself only renders the task; the segment is purely a
   *   canonical anchor (so deep-links from the email / stream inbox can carry both).
   */
  static forTasks(taskId?: string, options?: { conversationId?: string; layout?: Layout }): DockPointer {
    // Task is now a generic folder asset — it opens through the shared asset
    // editor (`editor/task/typeid/task-<id>`), not a bespoke ViewType.TASKS.
    // Delegating here transparently repoints every `forTasks` caller.
    const layout = options?.layout ?? Layout.DOCK;
    if (!taskId) return DockPointer.forAssetList('task', undefined, layout);
    const opts = options?.conversationId ? { conversationId: options.conversationId } : undefined;
    return DockPointer.forAssetEditorByTypeId('task', new TypeId('task', taskId), layout, opts);
  }

  /**
   * Create dock pointer for the dedicated conversation viewer at
   * `/dock/conversation/<conversationId>`. Same UI as the conversation
   * panel embedded in task views — the URL is just a different host for it.
   *
   * URL formats:
   *   /dock/conversation/<conversationId>
   *   /dock/conversation/<conversationId>/message/<messageId>
   *
   * The optional `message` segment deep-links to a specific FlowMessage —
   * the conversation view derives its selected bubble from it (URL-first)
   * and scrolls it into view.
   */
  static forConversation(
    conversationId: string,
    sub?: { messageId?: string | null; thread?: string | null; agentId?: string | null },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const pointer = sub?.messageId ? `${conversationId}/message/${sub.messageId}` : conversationId;
    // `thread` rides OPTIONS, not the path: opening a thread is a filter over
    // the same conversation, so `tabHash` must stay `conversation|<id>` and
    // never mint a second tab. Same call `forAutomations` and `forProcessRuns`
    // make, for the same reason.
    const options: Record<string, string> = {};
    if (sub?.thread) options[THREAD_PARAM] = sub.thread;
    if (sub?.agentId) options[AGENT_PARAM] = sub.agentId;
    return new DockPointer(ViewType.CONVERSATION, pointer, options, layout);
  }

  /** The thread this conversation dock is filtered to, or null for all. */
  get threadId(): string | null {
    return this.options?.[THREAD_PARAM] ?? null;
  }

  get agentScopeId(): string | null {
    return this.options?.[AGENT_PARAM] ?? null;
  }

  /** The view nested in this asset editor (see CHILD_PARAM), or null. */
  get child(): { section: ChildSection; typeId: string } | null {
    const typeId = this.options?.[CHILD_PARAM];
    const section = this.options?.[CHILD_SECTION_PARAM];
    return typeId && isChildSection(section) ? { section, typeId } : null;
  }

  /** This asset editor with `typeId` opened nested in it, from `section`. */
  withChild(section: ChildSection, typeId: string): DockPointer {
    return this.withOption(CHILD_PARAM, typeId).withOption(CHILD_SECTION_PARAM, section);
  }

  /** This asset editor with nothing nested in it — back to its own body. */
  withoutChild(): DockPointer {
    return this.withOption(CHILD_PARAM, null).withOption(CHILD_SECTION_PARAM, null);
  }

  /**
   * Parse a conversation pointer string.
   *
   * Accepted shapes:
   *   <conversationId>
   *   <conversationId>/message/<messageId>
   *
   * Returns nulls for segments that aren't present or the input is malformed.
   */
  static parseConversationPointer(pointer: string | undefined | null): {
    conversationId: string | null;
    messageId: string | null;
  } {
    if (!pointer) return { conversationId: null, messageId: null };
    const parts = pointer.split('/').filter(Boolean);
    const conversationId = parts[0] ?? null;
    const messageId = parts[1] === 'message' && parts[2] ? parts[2] : null;
    return { conversationId, messageId };
  }

  /**
   * Create dock pointer for the dedicated spec viewer at
   * `/dock/spec/<specId>`. Renders the Spec record's metadata and
   * a link back to the source plan + generated tasks.
   */
  static forSpec(specId: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.SPEC, specId, undefined, layout);
  }

  /**
   * The table behind one home counter: `group` names an asset-counter group
   * (`agents`), `counter` one of its counters (`running`). Both live in the
   * query, so the table the user sees IS the URL.
   */
  static forCounterAssets(group: string, counter: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.ASSET_LIST, undefined, { group, counter }, layout);
  }

  /**
   * Create dock pointer for the record search view
   * @param query - Optional search query string
   * @param filters - Optional filter options
   */
  static forSearch(
    query?: string,
    filters?: {
      record_type?: string;
      status?: string;
      scope?: string;
      time_preset?: string;
      time_start?: string;
      time_end?: string;
    },
    layout: Layout = Layout.DOCK,
  ): DockPointer {
    const opts: Record<string, string> = {};
    if (query) opts.q = query;
    if (filters?.record_type) opts.record_type = filters.record_type;
    if (filters?.status) opts.status = filters.status;
    if (filters?.scope) opts.scope = filters.scope;
    if (filters?.time_preset) opts.time_preset = filters.time_preset;
    if (filters?.time_start) opts.time_start = filters.time_start;
    if (filters?.time_end) opts.time_end = filters.time_end;
    return new DockPointer(ViewType.SEARCH, undefined, opts, layout);
  }

  /**
   * Create dock pointer for the fs-records scanner lens
   */
  static forFsRecordsScanner(layout: Layout = Layout.DOCK): DockPointer {
    return DockPointer.forLens('fs-records', 'scan', '', layout);
  }

  /**
   * Create dock pointer for the LLM Indexers lens — lists MarkdownIndex
   * entities, lets the user run / view each indexer.
   */
  static forLlmIndexers(layout: Layout = Layout.DOCK): DockPointer {
    return DockPointer.forLens('fs-records', 'llm-indexers', '', layout);
  }

  /**
   * Create a dock pointer for the transcript lens, dispatching by worker.
   *
   * - claude: ref is `<projectEncodedName>/<sessionId>` (legacy claude viewer).
   * - codex/copilot: ref is the URL-encoded absolute path to the
   *   rollout JSONL — the generic viewer fetches it via `useTranscript`.
   */
  static forLensTranscript(
    workerType: WorkerType,
    ref: string,
    layout: Layout = Layout.DOCK,
    options?: Record<string, string>,
  ): DockPointer {
    // Uniform across workers: encode. Claude used to be exempt so its legacy
    // two-segment ref (`<projectEncodedName>/<sessionId>`) kept its slash, but
    // nothing constructs that shape any more — claude now emits the same two
    // forms as codex/copilot (bare session id, or an absolute transcript path),
    // and encoding a bare UUID is a no-op. LensViewer still PARSES the legacy
    // form for old bookmarks.
    return DockPointer.forLens(workerType, 'transcript', encodeURIComponent(ref), layout, options);
  }

  /**
   * Create dock pointer for settings viewer
   * @param fieldName - Optional field name to scroll to / highlight
   * @param filter - Optional search filter string
   */
  static forSettings(fieldName?: string, filter?: string, layout: Layout = Layout.DOCK): DockPointer {
    const queryOptions: Record<string, string> = {};
    if (filter) queryOptions.filter = filter;
    return new DockPointer(ViewType.SETTINGS, fieldName, queryOptions, layout);
  }

  /**
   * Create dock pointer for the user Preferences screen.
   * @param category - Optional category whose tab should be active
   */
  static forPreferences(category?: string, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.PREFERENCES, category, {}, layout);
  }

  /**
   * Create dock pointer for the Credentials screen.
   *
   * The pointer grammar (`<subview>[/<projectId>]`) is not restated here —
   * `credentialsPointer` owns it, so a change there reaches every caller.
   * @param tab - Which tab is active; omitted lands on the caller's leading tab
   * @param projectId - Project whose environment is shown (Environment tab)
   * @param entry - The credential (typeid) shown selected — needs a project
   */
  static forCredentials(
    tab: CredentialsSubview = CredentialsSubview.CONNECTIONS,
    projectId?: string,
    layout: Layout = Layout.DOCK,
    entry?: string,
  ): DockPointer {
    return new DockPointer(ViewType.CREDENTIALS, credentialsPointer(tab, projectId, entry), {}, layout);
  }

  /**
   * Create dock pointer for the Data sources screen — its source list, its driver
   * list, or one driver. `dataSourcesPointer` owns the grammar.
   */
  static forDataSources(route?: DataSourcesRoute, layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.DATA_SOURCES, dataSourcesPointer(route), {}, layout);
  }

  /**
   * Create dock pointer for `projectId`'s schema tree with `path` selected — the kinds
   * unfolded down to the selected one (`schemaTreePointer` owns the grammar). The project
   * is the dock's scope, so the loader selects it and the tab belongs to it.
   */
  static forSchemaTree(projectId: string, path: string[] = [], layout: Layout = Layout.DOCK): DockPointer {
    return new DockPointer(ViewType.SCHEMA_TREE, schemaTreePointer(path), {}, layout).withScopeFilter(
      projectScope(projectId),
    );
  }

  /**
   * Check equality with another dock pointer
   */
  equals(other: DockPointer): boolean {
    return (
      this.viewType === other.viewType &&
      this.pointer === other.pointer &&
      this.page === other.page &&
      this.slot === other.slot &&
      this.layout === other.layout &&
      JSON.stringify(this.options) === JSON.stringify(other.options)
    );
  }

  /**
   * Canonical tab identity for this pointer, or **`null` when the surface is not
   * a tab at all** — the single generic "no tab" signal every consumer honors
   * (`ensureTabForCurrentDock` skips it; the strip's active-key resolves to no
   * chip). Two surfaces have no chip: a **full-bleed** view (e.g. `home`, via the
   * registry `chrome` flag) takes over the panel so there is no strip to sit in;
   * and a **bare shell** (`/dock/shell` with no session — the terminal HOST whose
   * sessions are the actual tabs at `/dock/shell/<session>`). Encoding "no tab"
   * here (not as a special case in each consumer) keeps the whole tab-or-not
   * decision in the one place that owns tab identity.
   *
   * Otherwise identity is ``viewType`` + ``pointer`` ONLY. ``layout`` is excluded
   * on purpose: a ``/win/`` popout and the ``/dock/`` view of the same content are
   * the same Tab (placement is per-client URL state, not tab identity). Transient
   * ``options`` (slot, query params) are excluded too — by default a viewType's
   * sub-state shares one tab (e.g. all settings sub-paths → one "Settings" tab).
   * This string is the natural key the backend stores verbatim as ``Tab.pointer``;
   * canonicalization lives here and NOWHERE else, so there is no cross-language
   * canonicalizer to keep in agreement.
   */
  get tabHash(): string | null {
    const base = this.baseTabHash;
    const host = this.workspaceHost;
    return base && host ? `${base}|@${host}` : base;
  }

  /** The workspace this dock is a tab OF (`isWorkspaceChildDock`), else null. Part of identity:
   *  each workspace has its own row for a thing, apart from that thing's top-level tab. */
  private get workspaceHost(): string | null {
    return isWorkspaceChildDock(this) ? this.hostProcessId : null;
  }

  private get baseTabHash(): string | null {
    if (!this.viewType) return null;
    // A full-bleed surface (Home) takes over the panel — no strip, hence no chip.
    if (VIEWER_REGISTRY[this.viewType]?.chrome === 'fullbleed') return null;
    // A bare shell is the terminal host; only a session-pointer shell is a tab.
    if (this.viewType === ViewType.SHELL && !this.pointer) return null;
    // Page namespaces tab identity: `desk` (the default) stays UNPREFIXED so every
    // existing persisted `Tab.pointer` key is byte-identical (no migration); a
    // non-desk page prefixes its id, giving each page its own tab namespace so a
    // `desk` tab and a `hub` tab with the same viewType/pointer never collide.
    const pagePrefix = this.page === PageId.DESK ? '' : `${this.page}|`;
    // A Vibe HOST dock folds onto its process's shell identity: one process is ONE
    // Tab row whichever way it is shown. The backend reconciles by this hash and
    // re-points the stored pointer in place (`ensure_tab`), so switching Vibe ⇄
    // Chat never mints a second chip.
    if (this.viewType === ViewType.VIBE) {
      return this.pointer ? `${pagePrefix}${ViewType.SHELL}|${this.pointer}` : null;
    }
    // A dependency home is one tab per provider/consumer pair. Its runtime options
    // remain presentation-only, but two consumers must never configure one another.
    if (this.options?.providerProject && this.options?.consumerProject) {
      return `${pagePrefix}dependency-home|${this.options.providerProject}|${this.options.consumerProject}`;
    }
    // A scope-keyed view (Assets, Explorer) is a SINGLE tab per scope: every
    // sub-pointer (asset type/folder/editor, explorer folder) of one scope folds
    // into ONE tab. Identity = the scope filter (global when unset), NOT the
    // sub-pointer. scopeFilterKey: 'all' | 'user' | 'project:<id>' |
    // 'filter:<0|1>:p1,p2'.
    if (VIEWER_REGISTRY[this.viewType]?.scopeKeyed) {
      return `${pagePrefix}${this.viewType}|${scopeFilterKey(this.scopeFilter ?? ALL_SCOPE_FILTER)}`;
    }
    // The two pointer-REDUCING folds (Preferences drops the pointer entirely,
    // Conversation/Apps keep the host segment). Both emit `plainKey`'s own shape
    // over a reduced pointer, so they share one arm — see `foldedPointer`.
    const folded = this.foldedPointer;
    if (folded !== null) {
      return `${pagePrefix}${this.viewType}|${folded}`;
    }
    return this.plainKey;
  }

  /**
   * The pointer TAB IDENTITY uses, for the two folds that merely reduce it:
   * `''` when the whole viewType is one tab (`foldsPointer`), the host segment
   * when each host is one tab (`foldsSubPointer`), and `null` when this view
   * does not fold at all — the signal to fall through to the plain form.
   *
   * Shared by `tabHash` and `toJSON` so the key and the persisted JSON cannot
   * disagree about which pointer a folded view is keyed on. The `scopeKeyed`
   * arm stays separate: its `toJSON` also carries `options` / `tabHash` /
   * `workspaceContent`, so it is not this shape.
   */
  private get foldedPointer(): string | null {
    const meta = this.viewType ? VIEWER_REGISTRY[this.viewType] : undefined;
    if (meta?.foldsPointer) return '';
    // A Project Home page (its setup page…) stays in the project's own tab.
    if (this.projectLandingPage) return DockPointer.splitProjectPointer(this.pointer).projectId ?? '';
    // First segment only: `<conversationId>/message/<id>` → `<conversationId>`.
    if (meta?.foldsSubPointer) return (this.pointer ?? '').split('/')[0];
    return null;
  }

  /** Identity for a dock that none of `tabHash`'s special arms claim: the page
   *  namespace, the viewType and the pointer. Named because `favoriteKey` needs
   *  the same string for a surface `tabHash` refuses to answer for at all. */
  private get plainKey(): string {
    const pagePrefix = this.page === PageId.DESK ? '' : `${this.page}|`;
    return `${pagePrefix}${this.viewType}|${this.pointer ?? ''}`;
  }

  /** The JSON form of {@link plainKey} — `toJSON`'s default arm, likewise shared
   *  with `toFavoriteJSON` so the shape `fromJSON` must accept is written once. */
  private get plainJSON(): string {
    return JSON.stringify({ viewType: this.viewType ?? '', pointer: this.pointer ?? '' });
  }

  /**
   * Identity for a FAVORITE — a different question from identity for a TAB, and
   * the app root is where the two answers diverge.
   *
   * Home is `chrome: 'fullbleed'`, so it is deliberately not a tab and `tabHash`
   * returns null for it; that null is load-bearing (nothing materializes a Tab
   * row for `/`, the strip highlights no chip). It is still a place you can want
   * to come back to. So this is `tabHash` without the full-bleed veto — the same
   * `plainKey` every ordinary dock gets — rather than a bespoke key invented
   * elsewhere and kept in sync by hand.
   *
   * Restricted to the root: a bare shell (`/dock/shell` with no session) is the
   * terminal HOST, and bookmarking it would bookmark nothing.
   */
  get favoriteKey(): string | null {
    return this.tabHash ?? (this.isRoot ? this.plainKey : null);
  }

  /** What a favorite of this dock stores, restored through `fromJSON` +
   *  `openDock`. Mirrors {@link favoriteKey}: `toJSON` when there is a tab, the
   *  plain form for the root, null for anything else. */
  toFavoriteJSON(): string | null {
    return this.toJSON() ?? (this.isRoot ? this.plainJSON : null);
  }

  /** This dock, presented in `projectId` (see {@link presentedIn}). */
  presentedInProject(projectId: string): DockPointer {
    const copy = new DockPointer(this.viewType, this.pointer, this.options, this.layout, this.page);
    copy.storedWorkspaceContent = this.storedWorkspaceContent;
    copy.presentedIn = projectId;
    return copy;
  }

  /** Serialize this dock's tab-identity fields (viewType + pointer) as JSON.
   *  This is what Tab.pointer stores in the DB. Returns null if tabHash is null. */
  toJSON(): string | null {
    const json = this.baseJSON();
    // A workspace tab stores its workspace-keyed hash: the stored pointer has no host, and the
    // backend reconciles by this hash (`_pointer_to_hash`), so it is the row's identity.
    const host = this.workspaceHost ? { tabHash: this.tabHash } : null;
    const presented = this.presentedIn ? { presentedIn: this.presentedIn } : null;
    if (!json || (!host && !presented)) return json;
    return JSON.stringify({ ...JSON.parse(json), ...host, ...presented });
  }

  private baseJSON(): string | null {
    // `viewType` is restated rather than left to `tabHash` (which already returns
    // null without one) so the registry lookups below narrow.
    if (!this.viewType || !this.tabHash) return null;
    // The Vibe host persists its REAL viewType with the folded (shell) hash: the
    // backend keys the row by the hash, and the stored viewType is what a chip
    // click reopens.
    if (this.viewType === ViewType.VIBE) {
      return JSON.stringify({ viewType: this.viewType, pointer: this.pointer ?? '', tabHash: this.tabHash });
    }
    // Dependency homes retain their execution provider and consuming-project context
    // in storage, so the backend and a reopened chip share the URL's pair identity.
    if (this.options?.providerProject && this.options?.consumerProject) {
      return JSON.stringify({
        viewType: this.viewType,
        pointer: this.pointer ?? '',
        options: this.options,
        tabHash: this.tabHash,
      });
    }
    // Scope-keyed identity is the SCOPE, not the sub-pointer. Normalize the pointer to
    // '' and persist the scope (options) + the computed tabHash so: (a) the stored
    // JSON is constant for a given scope regardless of which type was last viewed
    // → the backend mints ONE Tab row per scope; (b) `Tab.dockPointer` rebuilds the
    // same tabHash directly from the stored field; (c) clicking the chip reopens the
    // scoped browser root. Preserve only whether the live Assets URL addresses
    // content: the backend needs that URL-owned fact to validate a workspace
    // parent edge after the sub-pointer itself is folded out of tab identity.
    // It does not change tabHash, so editor/list URLs still share one row.
    if (VIEWER_REGISTRY[this.viewType]?.scopeKeyed) {
      // A dock rebuilt from stored JSON has already had its sub-pointer folded
      // to '' (and `fromUrl` supplied a default), so re-deriving the bit there
      // would invent one. Trust what was stored; derive only for a live URL.
      const workspaceContent =
        this.storedWorkspaceContent ??
        (this.viewType === ViewType.ASSETS &&
          (this.pointer?.startsWith(`${AssetMode.EDITOR}/`) || this.pointer?.startsWith(`${AssetMode.WIKI}/`)));
      return JSON.stringify({
        viewType: this.viewType,
        pointer: '',
        options: this.scopeFilter ? scopeFilterToDockOptions(this.scopeFilter) : undefined,
        tabHash: this.tabHash,
        workspaceContent: workspaceContent || undefined,
      });
    }
    // Pointer-folding views persist a constant identity: pointer normalized to the
    // folded form so the backend mints ONE row per fold unit — regardless of which
    // category was last viewed (Preferences), or which message / in-app route
    // happened to materialize the row (Conversation, Apps). Same intent as the
    // ASSETS scope-folding above, and the same reduction `tabHash` keys on.
    const folded = this.foldedPointer;
    if (folded !== null) {
      return JSON.stringify({ viewType: this.viewType, pointer: folded });
    }
    return this.plainJSON;
  }

  /** Deserialize a stored Tab.pointer JSON back to a navigable DockPointer.
   *  Replaces fromTabHash — no opaque string parsing needed. Returns null on malformed input. */
  static fromJSON(json: string): DockPointer | null {
    try {
      const parsed = JSON.parse(json) as {
        viewType?: string;
        pointer?: string;
        options?: Record<string, string>;
        workspaceContent?: boolean;
        presentedIn?: string;
      };
      const { viewType, pointer, options } = parsed;
      if (!viewType) return null;
      const normalized = normalizeWorldViewDockPointer(
        normalizeRetiredDockPointer({
          viewType: viewType as ViewType,
          pointer,
          options,
        }),
      );
      const dp = DockPointer.fromUrl(
        normalized.viewType ?? viewType,
        normalized.pointer || undefined,
        undefined,
        Layout.DOCK,
        normalized.page ?? PageId.DESK,
      );
      // Restore scope options (assets identity) so the reconstructed dock's
      // tabHash matches the live nav dock's.
      const restored = normalized.options
        ? new DockPointer(dp.viewType, dp.pointer, normalized.options, dp.layout, dp.page)
        : dp;
      // Carry the stored bit verbatim — absent means false, not "unknown", so a
      // toJSON → fromJSON → toJSON round trip is stable.
      restored.storedWorkspaceContent = parsed.workspaceContent === true;
      restored.presentedIn = parsed.presentedIn ?? null;
      return restored;
    } catch {
      return null;
    }
  }

  /**
   * The entity this dock targets, as a TypeId — for the tab's denormalized
   * target + project resolution (`Tab.getFromDockPointer`). Pure string parse,
   * no network. Two shapes carry an entity: the type lives IN the pointer
   * (`<type>-<id>` / `…/typeid/<type>-<id>`), or — for a bare-id pointer — in the
   * viewType segment (e.g. `/dock/project/<id>` → `project-<id>`,
   * `/dock/conversation/<id>`). vfs/list/folder/target-less docks → null.
   */
  get targetTypeId(): TypeId | null {
    const pointer = this.pointer;
    if (!pointer) return null;
    if (this.viewType === ViewType.WORLDVIEW) {
      const focus = this.options?.focus ?? null;
      const separator = focus?.indexOf(TypeId.DELIMITER) ?? -1;
      return separator > 0
        ? DockPointer.tryTypeId(focus!.slice(0, separator), focus!.slice(separator + TypeId.DELIMITER.length))
        : null;
    }
    if (this.viewType === ViewType.AGENT) {
      const { agentId, view } = DockPointer.parseAgentPointer(pointer);
      return view === 'stream_inbox' && agentId ? DockPointer.tryTypeId(Agent.type, agentId) : null;
    }
    if (this.viewType === ViewType.GRAPH) {
      const parsed = DockPointer.parseGraphPointer(pointer);
      return parsed ? DockPointer.tryTypeId(parsed.type, parsed.id) : null;
    }
    // A PLAN dock addresses its PLAN entity directly in the `typeid/<plan-id>`
    // form; the `vfs/<path>` form is path-resolved and carries no typeid target.
    if (this.viewType === ViewType.PLAN) {
      const parsed = DockPointer.parsePlanPointer(pointer);
      return parsed?.kind === 'typeid' ? parsed.planTypeId : null;
    }
    // A claude-transcript lens (`claude/transcript/<sessionId>`) targets its
    // ClaudeSession entity (id = session id). Surfacing it here puts lens on the
    // same entity rail as every other dock: the tab mint resolves the session's
    // name and the loader its project — no lens-special naming/project logic.
    if (this.viewType === ViewType.LENS) {
      const lens = DockPointer.parseLensPointer(pointer);
      if (lens?.category === 'claude' && lens.type === 'transcript' && lens.ref && !lens.ref.includes('/')) {
        return DockPointer.tryTypeId(ClaudeSession.type, lens.ref);
      }
    }
    // A live-session dock targets its RemoteWorkerSession entity (id = session
    // id). The viewType STRING ('live_session') differs from the entity TYPE
    // ('remote_worker_session'), so it must be surfaced explicitly — the generic
    // fallback below would mint the tab against a non-existent 'live_session'
    // target, leaving it untitled and projectless (Global-scoped). Puts the
    // session on the same entity rail as every other tab: the mint resolves its
    // title (host/guest) and the loader its project.
    if (this.viewType === ViewType.LIVE_SESSION) {
      return DockPointer.tryTypeId(RemoteWorkerSession.type, pointer);
    }
    // Same again for a diagnosis: the viewType string ('diagnosis') is not the
    // entity type ('flowpad_diagnosis'), so the fallback named a target that
    // does not exist and the tab had no entity to title or describe.
    if (this.viewType === ViewType.DIAGNOSIS) {
      return DockPointer.tryTypeId(FlowpadDiagnosis.type, pointer);
    }
    // A helpdesk dock targets the portal PROJECT it renders. Same reason as
    // live_session above: the viewType string ('helpdesk') is not an entity
    // type, so the generic fallback would mint the tab against a non-existent
    // 'helpdesk' target — untitled and projectless. The article sub-pointer is
    // deliberately dropped: every article belongs to the one portal project, so
    // the tab stays on the portal (see `foldsPointer` in the viewer registry).
    if (this.viewType === ViewType.HELPDESK) {
      const { projectId } = DockPointer.parseHelpdeskPointer(pointer);
      return projectId ? DockPointer.tryTypeId(Project.type, projectId) : null;
    }
    // A PROJECT-rebased asset dock (`/dock/project/<id>/<assetSubPointer>`, the
    // output of `rebaseAssetsOntoProject`) carries its target in the asset
    // sub-pointer, addressed exactly as a plain ASSETS dock would. A
    // typeid-addressed asset surfaces its entity here; a vfs/list/folder/wiki
    // sub-pointer carries no typeid target (its entity is resolved by path via
    // `vfsPath`) — and crucially we must NOT surface the `<id>` project segment
    // as the target, so the path-resolved asset's OWN project wins on the tab.
    const assetSub = this.assetSubPointer;
    if (this.projectLandingPage) {
      const { projectId } = DockPointer.splitProjectPointer(pointer);
      return projectId ? DockPointer.tryTypeId(Project.type, projectId) : null;
    }
    if (assetSub !== null) {
      const typeid = this.assetEditorValue(assetSub, AssetRoutingMethod.TYPEID);
      return typeid ? DockPointer.tryTypeId(typeid) : null;
    }
    // A K_BROWSER dock is `<method>/<value>` (`typeid/<type>-<id>` | `vfs/<path>`),
    // so its value must be split off by the parser that owns that grammar. Without
    // this branch it fell to the generic fallback below, whose `/typeid/` test only
    // matches an EMBEDDED marker — a pointer that *begins* `typeid/` slips past it,
    // and `new TypeId('typeid/markdown-<uuid>')` then splits on the first `-` and
    // yields type `typeid/markdown`. That produced `GET /graph/typeid/markdown/<uuid>`
    // → 422, plus the `TypeId null` follow-ons, on every k-browser typeid address.
    if (this.viewType === ViewType.K_BROWSER) {
      const kb = DockPointer.parseKnowledgeBrowserPointer(pointer);
      return kb?.method === 'typeid' ? DockPointer.tryTypeId(kb.value) : null;
    }
    const candidate = pointer.includes('/typeid/') ? (pointer.split('/typeid/').pop() ?? '') : pointer;
    return (
      DockPointer.tryTypeId(candidate) ??
      (this.viewType && !pointer.includes('/') ? DockPointer.tryTypeId(this.viewType, pointer) : null)
    );
  }

  /**
   * The inner asset sub-pointer of a PROJECT-rebased asset dock
   * (`/dock/project/<id>/<assetSubPointer>`) — the same shape a plain
   * `/dock/assets/<sub>` dock carries. Null when this isn't a project dock
   * carrying a sub-pointer (a bare `/dock/project/<id>` has none). This is the
   * un-rebase that lets the tab-mint getters treat a project-shell asset URL
   * identically to a plain assets URL, so the asset's own project is resolved.
   */
  /** A Project Home page addressed under the project dock (`/dock/project/<id>/project-setup`): its row in
   *  `PROJECT_LANDING_PAGES`, else null. Such a page is the project's own tab and names the project. */
  get projectLandingPage(): ProjectLandingPage | null {
    return projectLandingPageOf(this.assetSubPointer, { underProjectDock: true });
  }

  private get assetSubPointer(): string | null {
    if (this.viewType !== ViewType.PROJECT || !this.pointer) return null;
    const { assetSubPointer } = DockPointer.splitProjectPointer(this.pointer);
    return assetSubPointer || null;
  }

  /**
   * Parse an asset sub-pointer and return the `value` of an `editor/<...>/<method>`
   * pointer when it matches `method` (`typeid` → a `<type>-<id>` string, `vfs` →
   * a vfs path), else null. The shared parse-and-match both `targetTypeId` and
   * `vfsPath` use to read their respective addressing form off the same pointer.
   */
  private assetEditorValue(pointer: string | null, method: AssetRoutingMethod): string | null {
    return assetEditorValue(pointer, method);
  }

  /**
   * Canonical filesystem identity addressed by this route, independent of the
   * view that renders it. Pure string parsing; no network or request context.
   *
   * Supported route families:
   * - Assets editor: `editor/<editor>/vfs/<absVfsPath>`
   * - Assets files:  `fs/vfs/<absVfsPath>`
   * - project-rebased variants of both
   * - Explorer: `<absVfsPath>`
   * - Plan: `vfs/<absVfsPath>`
   * - raw editor when its pointer already carries an absolute VFS path
   */
  get resourceVfsPath(): VFSPath | null {
    const assetsPointer = this.viewType === ViewType.ASSETS ? (this.pointer ?? null) : this.assetSubPointer;
    if (assetsPointer) {
      const fsPath = DockPointer.parseAssetFsPointer(assetsPointer);
      if (fsPath) return fsPath;

      const editorValue = this.assetEditorValue(assetsPointer, AssetRoutingMethod.VFS);
      if (editorValue) {
        const parsed = VFSPath.parse(editorValue);
        return parsed.isAbsolute ? parsed : null;
      }
    }

    if (this.viewType === ViewType.PLAN && this.pointer) {
      const plan = DockPointer.parsePlanPointer(this.pointer);
      if (plan?.kind === 'vfs') {
        const parsed = VFSPath.parse(plan.vfsValue);
        return parsed.isAbsolute ? parsed : null;
      }
    }

    if ((this.viewType === ViewType.EXPLORER || this.viewType === ViewType.EDITOR) && this.pointer) {
      const parsed = VFSPath.parse(this.pointer);
      return parsed.isAbsolute ? parsed : null;
    }

    return null;
  }

  /**
   * @deprecated use `resourceVfsPath`, whose name reflects that VFS identity is
   * shared across editor, Files, Explorer, and Plan route grammars.
   */
  get vfsPath(): VFSPath | null {
    return this.resourceVfsPath;
  }

  /**
   * The wiki space + word this route addresses, for a `wiki/…` dock.
   *
   * The THIRD addressing form, alongside `targetTypeId` and `resourceVfsPath`,
   * and the only one that names its subject rather than identifying it: a wiki
   * route stays at the word so it survives a rename, which is exactly why the
   * other two return null here. Anything that wants to say where a wiki route
   * points reads this — the word is a usable label with no lookup at all, and
   * the space is the Wiki entity's own id.
   *
   * Covers the project-rebased form (`/dock/project/<id>/wiki/…`) through the
   * same `assetSubPointer` un-rebase the other two getters use.
   */
  get wikiRef(): AssetWikiRef | null {
    return parseAssetWikiRef(this.viewType === ViewType.ASSETS ? this.pointer : this.assetSubPointer);
  }

  /**
   * The asset EDITOR this route opens, or null. Answers WHICH EDITOR, not what
   * subject: the segment is in the URL for both routing methods, so a caller
   * learns it with no entity resolution and no async gap.
   */
  get assetEditor(): AssetEditor | null {
    return assetEditorOf(this.viewType === ViewType.ASSETS ? this.pointer : this.assetSubPointer);
  }

  /**
   * True when this dock addresses the PROJECT ITSELF, not something inside it.
   *
   * `viewType === PROJECT` is NOT that question: `/dock/project/<id>/editor/…`
   * and `/dock/project/<id>/wiki/…` are project-REBASED asset routes that
   * address an asset and merely render in the project shell. Anything treating
   * the bare viewType as "the project page" mislabels every one of them — the
   * address bar called them all "Home".
   */
  get isProjectShell(): boolean {
    return this.viewType === ViewType.PROJECT && this.assetSubPointer === null;
  }

  /** DEPRECATED: use fromJSON instead. Reconstruct the navigable DockPointer from a
   *  legacy stored `Tab.pointer` (`viewType|sub` string format). Null when invalid.
   *  This method remains for backward compatibility but new code should use fromJSON. */
  static fromTabHash(hash: string): DockPointer | null {
    const i = hash.indexOf('|');
    const viewType = i >= 0 ? hash.slice(0, i) : hash;
    const sub = i >= 0 ? hash.slice(i + 1) : '';
    try {
      return DockPointer.fromUrl(viewType, sub || undefined);
    } catch {
      return null;
    }
  }

  /**
   * Convert to URL segments (for building URLs)
   */
  toUrlSegments(): { viewType: ViewType; pointer?: string; layout: Layout; page: PageId } {
    return {
      viewType: this.viewType!,
      pointer: this.urlParts.pointer,
      layout: this.layout,
      page: this.page,
    };
  }

  /**
   * This dock as it is SERIALIZED — the stored pointer with the host put back as
   * path segments where it can nest, and the query options that go with that
   * choice. The inverse of `fromUrl`'s lift.
   *
   * Both halves come from one branch on purpose: the host must be stripped from
   * the query exactly when the path absorbed it. Deriving that twice and
   * comparing the results would make it a coincidence rather than a fact.
   * Everything that serializes goes through here; everything that reads identity
   * or content uses `pointer`.
   */
  private get urlParts(): { pointer?: string; options?: Record<string, string> } {
    const withHost = this.hostUrlParts;
    const child = this.child;
    if (!child || !withHost.pointer) return withHost;
    // The nested child goes last in the path, after the host (lifted first on the way back in).
    const { [CHILD_PARAM]: _child, [CHILD_SECTION_PARAM]: _section, ...rest } = withHost.options ?? {};
    return { pointer: `${withHost.pointer}/${CHILD_SEGMENT}/${child.section}/${child.typeId}`, options: rest };
  }

  private get hostUrlParts(): { pointer?: string; options?: Record<string, string> } {
    const host = this.hostProcessId;
    const embedded =
      host && this.viewType === ViewType.PROJECT ? embedHostInProjectPointer(this.pointer, host) : undefined;
    // A hosted dock with no project segment to nest under (a terminal, a web
    // app) keeps the plain `?host=` form rather than losing the host.
    if (!embedded || embedded === this.pointer) return { pointer: this.pointer, options: this.options };
    const { [HOST_PARAM]: _host, ...rest } = this.options ?? {};
    return { pointer: embedded, options: rest };
  }

  /**
   * Serialize this DockPointer into the canonical layout URL.
   */
  toUrl(currentPath: string = ''): string {
    if (!this.viewType) {
      throw new NavigationError(NavigationErrorType.UNKNOWN_VIEW, 'Cannot serialize DockPointer without a view type');
    }
    const { pointer, options } = this.urlParts;
    return buildDockUrl(currentPath, this.viewType, pointer, options, this.layout, this.page);
  }

  /**
   * Convert options to URLSearchParams
   */
  toSearchParams(): URLSearchParams {
    const { options } = this.urlParts;
    if (!options) {
      return new URLSearchParams();
    }
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.set(key, value);
      }
    });
    return params;
  }

  /**
   * Get human-readable description of this dock pointer
   */
  toString(): string {
    const optionsStr = this.options && Object.keys(this.options).length > 0 ? ` (${JSON.stringify(this.options)})` : '';
    return `DockPointer(${this.viewType}/${this.pointer}, slot: ${this.slot}${optionsStr})`;
  }

  /**
   * Get the current slot from options, defaulting to TAB if not set
   */
  get slot(): ViewSlot {
    const slotValue = this.options?.slot;
    if (!slotValue || !Object.values(VIEW_SLOTS).includes(slotValue as ViewSlot)) {
      return VIEW_SLOTS.TAB;
    }
    return slotValue as ViewSlot;
  }

  /**
   * Set the slot in options (validated)
   */
  set slot(value: ViewSlot) {
    if (!Object.values(VIEW_SLOTS).includes(value)) {
      throw new Error(`Invalid slot: ${value}. Must be one of: ${Object.values(VIEW_SLOTS).join(', ')}`);
    }
    if (this.options) {
      this.options.slot = value;
    }
  }
}

/**
 * Compute the project-scoped DockPointer for a terminal tab rendered inside
 * the Project (collaboration) view. Prefers the AgenticProcess when the tab
 * is a Claude session; falls back to the plain Shell.
 *
 * The room id is required — tabs inside a collaboration room always belong
 * to one. Keep `process.dockPointer` untouched — this function is the seam.
 */
export function getProcessProjectDockPointer(
  tab: { shell?: { id?: string } | null; agenticProcess?: { id?: string } | null },
  projectId: string,
  roomId: string,
): DockPointer {
  if (tab.agenticProcess?.id) {
    return DockPointer.forProject(projectId, {
      roomId,
      tab: new TypeId('agentic_process', tab.agenticProcess.id),
    });
  }
  if (tab.shell?.id) {
    return DockPointer.forProject(projectId, {
      roomId,
      tab: new TypeId('shell', tab.shell.id),
    });
  }
  return DockPointer.forProject(projectId, { roomId });
}
